import * as React from 'react';
import React__default, { useState, useEffect } from 'react';
import { useAuth } from '@izocloud/auth';
import { useAnalytics, events } from '@izocloud/analytics';
import Lottie from 'react-lottie-player';

/**
 * component: Button
 *
 * Summary:
 * Used for all clickable elements, regardless of appearance
 *
 * Description:
 * Button elements should be used for anything that the user clicks
 * in order to support accessibility. By default, the button component
 * will support screen readers and keyboard based interactions,
 * and therefore should always be used instead of simply adding
 * an onClick method to a basic HTML element.
 *
 * @param type: solid, outline, text
 * Defines the appearance of the button
 * `solid` should be used for primary actions
 * `outline` can be used for secondary interactions
 * `text` can be used for non-focused clickable elements, as well as links
 *
 * @param size: small, medium, large
 * welp this one seems pretty straightforward....
 *
 * @param color: white, black, blue, red, grey
 * defines the background or text color of the button
 *
 * @param id: string
 * Used to apply a custom ID property to the HTML button element
 *
 * @param className: string
 * Used to apply a list of custom classes to the HTML button element
 *
 * @param onClick: function
 * Callback to be applied to click and key press event listeners
 *
 * @return HTML <button> element
 */
const Button = ({ children, type = 'primary', size = 'medium', color = 'white', id, className, fullWidth, onClick, disabled, }) => {
    // add default classes to optional custom className
    className = `izo-button type-${type} size-${size} color-${color}${fullWidth ? ' full-width' : ''} ${className || ''}`;
    const handler = disabled ? undefined : onClick;
    return (React__default.createElement("button", { type: 'button', id: id, className: className, onClick: handler, disabled: disabled }, children));
};

const ExternalLink = ({ children, to, fullWidth, }) => (React__default.createElement("a", { href: to, className: 'izo-external-link', rel: 'noopener noreferrer', target: '_blank', style: fullWidth ? { width: '100%' } : undefined }, children));

var img = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABLAAAASwCAYAAADrIbPPAAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAUGVYSWZNTQAqAAAACAACARIAAwAAAAEAAQAAh2kABAAAAAEAAAAmAAAAAAADoAEAAwAAAAEAAQAAoAIABAAAAAEAAASwoAMABAAAAAEAAASwAAAAAFK5rHgAAAFZaVRYdFhNTDpjb20uYWRvYmUueG1wAAAAAAA8eDp4bXBtZXRhIHhtbG5zOng9ImFkb2JlOm5zOm1ldGEvIiB4OnhtcHRrPSJYTVAgQ29yZSA2LjAuMCI+CiAgIDxyZGY6UkRGIHhtbG5zOnJkZj0iaHR0cDovL3d3dy53My5vcmcvMTk5OS8wMi8yMi1yZGYtc3ludGF4LW5zIyI+CiAgICAgIDxyZGY6RGVzY3JpcHRpb24gcmRmOmFib3V0PSIiCiAgICAgICAgICAgIHhtbG5zOnRpZmY9Imh0dHA6Ly9ucy5hZG9iZS5jb20vdGlmZi8xLjAvIj4KICAgICAgICAgPHRpZmY6T3JpZW50YXRpb24+MTwvdGlmZjpPcmllbnRhdGlvbj4KICAgICAgPC9yZGY6RGVzY3JpcHRpb24+CiAgIDwvcmRmOlJERj4KPC94OnhtcG1ldGE+Chle4QcAAEAASURBVHgB7N0JeF1lmTjwJt1p2REKFKhYQCktaNVaRIkDgkUKiAadIn8KMqAgI7Ip4BIGYRRHcVDGAUFEEaQoiEBZRFqQTVqWQlug+0L3pmnTNk3TpP2/h2kwTbPcJPfe3HvzO8/zPffes3zf+/2+k8p5/c453bpZCBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBDYXqBo+1XWECBAgAABAgQIpCKwZcuW7rHfflE+FOWIKEdGGRClV5TNUV6NMmXr58vxuaKoqChZbyFAgAABAgQIECBAgAABAgQIECCQGYFIWhVFeV+UO6O0Z7kxDhoUxf+RmJkhUisBAgQIECBAgAABAgQIECBAoOsKRNLp/VHejJKOZU5UMjQ0JbK67iml5wQIECBAgAABAgQIECBAgACB9AhEoqlHlJvSkbVqoo4nYt1O6YlULQQIECBAgAABAgQIECBAgAABAl1OIJJL/aNMbyLxlM5VNVHZYV0OV4cJECBAgAABAikKmLKeIpTdCBAgQIAAga4nEEmlXaLXC6LsmKXeHxEPeU8e+m4hQIAAAQIECBBoICCB1QDDVwIECBAgQIBAvUAkr3aI77OjJG8VzObyvkhizclmg9oiQIAAAQIECOS6QHGuByg+AgQIECBAgEC2BSJ5lfw30nNRsp28Srr6WrTfN/liIUCAAAECBAgQ+D8BCSxnAgECBAgQIEBge4GrY9Xh26/Oypp+0cqfIollpnxWuDVCgAABAgQI5IOA/zDKh1ESIwECBAgQIJA1gUgcHRKNvZm1Bptv6NNxK+ETzW+2hQABAgQIECDQdQQksLrOWOspAQIECBAg0IrA1lsHZ8Ru72tl12xsXh+N7BpJrE3ZaEwbBAgQIECAAIFcFnALYS6PjtgIECBAgACBbAscHw3mQvIq6XdyK+GZyRcLAQIECBAgQIAAAQIECBAgQIAAgW5lZWXFMQNrSZRcWqoimO6GhwABAgQIECDQ1QXcQtjVzwD9J0CAAAECBN4RiETREfHllRzkKInbCJ/KwbiERIAAAQIECBDImoBbCLNGrSECBAgQIEAgxwW+maPxfTdH4xIWAQIECBAgQCBrAmZgZY1aQwQIECBAgECuCsTsqx4RWy4/LL1PzMLamKt+4iJAgAABAgQIZFrADKxMC6ufAAECBAgQyAeBgTke5EE5Hp/wCBAgQIAAAQIZFZDAyiivygkQIECAAIE8EfhAjsc5LMfjEx4BAgQIECBAIKMCElgZ5VU5AQIECBAgkCcCI3M8zk/meHzCI0CAAAECBAhkVEACK6O8KidAgAABAgTyRGC/HI/z4ByPT3gECBAgQIAAgYwKSGBllFflBAgQIECAQJ4I1OVJnMIkQIAAAQIECHRJAQmsLjnsOk2AAAECBAg0EljY6Heu/VyaawGJhwABAgQIECCQTQEJrGxqa4sAAQIECBDIVYHXcjWwrXEtyPH4hEeAAAECBAgQyKiABFZGeVVOgAABAgQI5InAlByP8+0cj094BAgQIECAAIGMCkhgZZRX5QQIECBAgEA+CEycODGnZzjV1NSszAdHMRIgQIAAAQIECBAgQIAAAQIECGRQYMuWLX+KkqvLulgOz2D3VU2AAAECBAgQIECAAAECBAgQIJDrApG5+lCuZq8axPXd+G4Gfa6fTOIjQIAAAQIECBAgQIAAAQIECGRCIEkMRalokCzK1a+vzp07d5dMGKiTAAECBAgQIJCrAkW5Gpi4CBAgQIAAAQLZFoiMVWm0OS7b7bajvc21tbWf7tmz55PtONYhBAgQIECAAIG8EzAFPe+GTMAECBAgQIBApgTuvffe+6LupZmqP431Fvfo0eNvkXD73wkTJvRIY72qIkCAAAECBAjkpIAZWDk5LIIiQIAAAQIEOksgkkIfjrYndVb77Wh3YXl5+cg99thjUTuOdQgBAgQIECBAIC8EzMDKi2ESJAECBAgQIJAtgaKiosnR1v9kq700tLPf7rvv/nYk3sZE8X9OpgFUFQQIECBAgAABAgQIECBAgACBnBeIRFCPKPOi5NvyQDzgvU/OAwuQAAECBAgQINBGAf8vXRvB7E6AAAECBAh0DYHIXO0cPV0YZcc86/HampqaEb17934jz+IWLgECBAgQIECgWQG3EDZLYwMBAgQIECDQlQXiVsI169atGxwGG/PMYcdevXpNjwTcJWVlZf5bL88GT7gECBAgQIAAAQIECBAgQIAAgTYLVFRU7BLJoBn5di/h1nifW7FiRb7NIGvzGDmAAAECBAgQKHwBtxAW/hjrIQECBAgQINBBgUgG9Ygqbovy/zpYVWccvikaPTpmlD3fGY1rkwABAgQIECCQDgHTytOhqA4CBAgQIECgoAUi+VMb5czo5EeiLE9XZzdv3pyuqlqqp2dsfC6ScD+O0r2lHW0jQIAAAQIECBAgQIAAAQIECBAoAIFx48Z1j0RQSZRnorRnqYqD7rzvvr+ceORRR5/15ISJ7amjvce8Wblly+4FMAy6QIAAAQIECHQxAbcQdrEB110CBAgQIEAgfQKRRUpuLdwpSvLGwn5RekdJZrgnU6tqotRG2RClamupTmZzxfd3l9GjR7//L3/5S7bfGDgq4ngsgtjybiC+ECBAgAABAgQIECBAgAABAgQIEGhO4O/PPXdB/ZSquK2w/mumP2+bPHlycnuhhQABAgQIECBAgAABAgQIECBAgEDLAmVlZcWRrZqTZKxefHHSlqf/3t67E9uc81pUVVW1b8vR2UqAAAECBAgQIECAAAECBAgQIEAgBCZNmnRYffrp5M99Ycu1//mjLbW1tfWrMv35xWjAoyWciQQIECBAgAABAgQIECBAgAABAi0LRBJpfJKpmvLaa1ve855BA0adeNLXFy58O1mVjeXeaKRXyxHaSoAAAQIECBAgQIAAAQIECBAg0KUFpk2bNqA+U/XvF198TIKx/9Chu/7loYezdU/hig0bNry3Sw+CzhMgQIAAAQIECBAgQIAAAQIECLQsEAms25Ik1vLlKypiz+5b9y665PLLT4/kUn1+K9OfX4kG3FLY8lDZSoAAAQIECBAgQIAAAQIECBDomgLz58/ftT47deONN53eUGHEiGP2euONN2fUb8/w54Nz587t07B93wkQIECAAAECBAgQIECAAAECBAi8IxCJqZ8lyamNGzfWDCopaZxE6v6b3955XYaTV/XVV8SXgwwLAQIECBAgQIAAAQIECBAgQIAAgW0EZs+evXN9Bumhh8Z/d5uNW3+ceeY5w9esWVNdv1+GP78a9bulsKmBsI4AAQIECBAgQIAAAQIECBAg0FUFImH04/qk1CmnnLJLUw7Dhg3r9+KLkybW75fhz0fdUtjUKFhHgAABAgQIECBAgAABAgQIEOiiAjNnztypPiE1derUO1pgKPrpz3729bq6uvrdM/lZWV1dfXALsdhEgAABAgQIECBAgAABAgQIECDQlQQiE3V9fTbqG9/41v4t9f1zn/viwUuXLUueWZWN5WvRiFsKWxoQ2wgQIECAAAECBAgQIECAAAECXUGgvLz83VlYK1aseCb63GLSaNCgQX0e++sTD2YjgxVtuKWwK5yE+kiAAAECBAgQIECAAAECBAgQaE0gEkU/qU9I3fCLX3yotf1je9H3rr7mrE2bNtUflsnPiril0FsKUxgUuxAgQIAAAQIECBAgQIAAAQIEClZg1apV776RMJJF86Kjxal09rjRo9/79ttvr8hk9qpB3f8W31ucHZZKzPYhQIAAAQIECBAgQIAAAQIECBDIU4FIDv2sPln0xz/+eXSq3Rg8eHDvRx9//E/1x2b488F48HzvVGOzHwECBAgQIECAAAECBAgQIECAQAEJrF69etf65NPmzZurhg8f3rMN3Su6+uofnJGlWwpXbNiwYVAbYrMrAQIECBAgQIAAAQIECBAgQIBAoQhEAuuX9UmsZ59//sK29usznzllUBZvKRwTsbqlsK2DZH8CBAgQIECAAAECBAgQIECAQD4LVFZW7l6fwEo+S0vP79/W/gwZMqTX44//NVu3FN41efKWtswUa2t37E+AAAECBAgQIECAAAECBAgQIJBrApG3uq0+iTVj1qxftjO+eEvhD87M0i2Fi9atWzegnXE6jAABAgQIECBAgAABAgQIECBAIN8Eli5dumd9Aiv5vPLKK/dubx8++9nPHhi3FJY3rC+D309ob5yOI0CAAAECBAgQIECAAAECBAgQyDOBSDLdVZ9oWrmy/PGOhD9o0KA+jz3++MP19WX486aov3tH4nUsAQIECBAgQIAAAQIECBAgQIBAHggkt+Q1TDTdeOONh3Yw7KKysv/4apZuKZxeUVGxSwfjdTgBAgQIECBAgAABAgQIECBAgECuC0QC690HsW/YsOHNiLfDb/z7zGdOOiRuKaxsmBzL0PeaqPejuW4sPgIECBAgQIAAAQIECBAgQIAAgQ4IVFVV7dswuXTvvfeWdKC6dw8dOHBk33hL4RMN687g9yvKysqK323cFwIECBAgQIAAAQIECBAgQIAAgcISiMTS+Prk0ubNm8tLS0vT9Xypov/4jx9cVFtbW199Jj+fXLhwYd/CGhm9IUCAAAECBAgQIECAAAECBAgQeEcgbh08oGFmacJTT52eTpqTTz556LJly6satpGh7xXV1dUHpjN2dREgQIAAAQIECBAgQIAAAQIECOSIQCSUnmyQVKoZNerC3ukMbdiwYf2ee/6FZxq0kcmvYyL2Dj/LK539VxcBAgQIECBAgAABAgQIECBAgEAHBdasqR7cMKP02muvfaeDVTZ1ePFNN/3yuw3byeD3OyZMmNCjqSCsI0CAAAECBAgQIECAAAECBAgQyE+BokgmvdAwoXTuuefunImujB17zsfWrl2bvEEw08usNWvW7JaJPqiTAAECBAgQIECAAAECBAgQIECgEwQ2btz4gYYZpfnzF9yeqTCOOKJkl6lTp01v2F6GvtfV1NR8NFP9UC8BAgQIECBAgAABAgQIECBAgEB2BZJZWK82TCSVlZUNzGAI3e/6wz03Nmwvg98vjbo9FyuDg6lqAgQIECBAgAABAgQIECBAgEBWBGK20uENk0grV66cGA1nNPFz8cWXnRBvD2zYbKa+j585c2ZaH06flUHRCAECBAgQIECAAAECBAgQIECAwD8FkllKUWY0zCD99Oc/H/rPPTLz7RPHH7/3vHnzlzRsN0Pfl61fv36fzPRCrQQIECBAgAABAgQIECBAgAABAlkRiFlYH2mYPKqqqpoVDRdnuvEhQ4b0evSxv97fsO0Mfj8+0/1RPwECBAgQIECAAAECBAgQIECAQIYEImmUzMKa1zB59Pvf//7YDDXXuNqia6657rza2tqGzWfq+w/LtmzJeGKucQf9JkCAAAECBAgQIECAAAECBAgQSIPApk2bPtEwa1RXV1dRUlLSIw1Vp1TFyaWlhy5fvqKqYQwZ+v70woUL+6YUlJ0IECBAgAABAgQIECBAgAABAgRyRyDePlgcCaNtnkn1yCOPj81mhHFLYf9//GPS5AwlrhpWWx63Se6Xzb5piwABAgQIECBAgAABAgQIECBAIA0CMQvr2IZZnvheV1Iytk8aqm5LFcW//s0dP24UR6Z+fqYtgdmXAAECBAgQIECAAAECBAgQIECgkwXGjRvXPTJFFQ2zRZMmvVTWGWF9/RuXHL9hw4aGoWTq+3XJ7LPO6KM2CRAgQIAAAQIECBAgQIAAAQIE2iEQD1M/uXGmaMyYMbu2o6oOH3L88cfvvWDBwm1ua2wcW5p+Pz137txszzTrsI8KCBAgQIAAAQIECBAgQIAAAQJdUmDChAk9Iim0zcPUZ82adXtnYcRzsXo99dTTD6cpUdVSNclzsfbtrH5qlwABAgQIECBAgAABAgQIECBAoA0CkeX5cuNMz+WXX75PG6pI965Fv/jF/1zcOKZM/I7ngH0q3cGrjwABAgQIECBAgAABAgQIECBAIM0C8SysXpEcqmuYIFq2bNmjaW6mzdWdfd55H16/vqqmYVwZ+v6tqLeozQE6gAABAgQIECBAgAABAgQIECBAIHsCkcD5WuPk0HXX/eTg7EXQdEtHHXXUrrPnzJnROLYM/L5/8uTJPZuOwloCBAgQIECAAAECBAgQIECAAIFOF4hnYfVpnBSqXLv21Qis02cmlZSU9Hjsr0/c2Ti+DPyes3r16k55gH2nnwACIECAAAECBAgQIECAAAECBAjkg0AkhJJb6bZZbrnl9o/kSOxF11//X+ds3rx5m/gy8KNm48aNQ3Okz8IgQIAAAQIECBAgQIAAAQIECBBoKPDYY4/1a5wQqq6uXhD7FDfcrzO/f/GMMw6rrKysbhxnBn6fEf3s9NlnnWmtbQIECBAgQIAAAQIECBAgQIBATgpEIuiHjZNBv7/nnuNzKdgRI0bs9MYbb0xtHGcGft8YdeZM8i6XxkAsBAgQIECAAAECBAgQIECAAIFOE4hnYe3SOBFUW1tbmTyHqtOCarrh7g888OBtjWPNwO+nZ86c2bvpEKwlQIAAAQIECBAgQIAAAQIECBDoFIFIAt3SOBH00EMPfblTgmm50aJr//P6sVl4LtaS8Nij5VBsJUCAAAECBAgQIECAAAECBAgQyJrAlClT9mycwIrfNaWlpb2yFkQbGjojnou1dt26TD8Xqy4MPtiGsOxKgAABAgQIECBAgAABAgQIECCQSYFI1vypcRJrwoSnvpHJNjtS9/Dhx+48a86c6Y1jzsBvD3fvyEA5lgABAgQIECBAgAABAgQIECCQLoFps2fv31TyZ/To0Tukq41015M8pyuSbHc2FXea1/0s6vNw93QPoPoIECBAgAABAgQIECBAgAABAm0ViCTN040TP5MmvXRtW+vJ8v5Fv/zlzV9rHHcGfj8xbdq0nLylMsvemiNAgAABAgQIECBAgAABAgQIdJ7A3LmL399U4mfs2LG7dF5UqbX8tX//9+HV1dXJc6syucxZtWrVzqlFZC8CBAgQIECAAAECBAgQIECAAIFMCBRF9ue1xhmgN95445ZMNJbuOo855pjdFy9ZMq9x/Gn+XRWJsvelO3b1ESBAgAABAgQIECBAgAABAgQIpCiwePGK4U0lfM455xt7pVhFp+42ZMiQXpMnT364qT6ked2xndpRjRMgQIAAAQIECBAgQIAAAQIEuqpAJHmSWVgLGid7FixYcH8emRTdfffd323chwz8vijqLMojF6ESIECAAAECBAgQIECAAAECBApDoKKioqSpZM9VV111QD718Mrvfe/42traprqSznW3RmXeUJhPJ4ZYCRAgQIAAAQIECBAgQIAAgfwXKCsrK46kTHnjLM/SpcsnRu/yasZRaWnp/uXl5ZWN+5Lm3xOjPm8ozP9TXw8IECBAgAABAgQIECBAgACBfBJYv379yU0lea677rr351M/kliHDRvW780333q1qf6kcd2cSJTtlG824iVAgAABAgQIECBAgAABAgQI5K3AhAkTekRyp7pxgmf16tUvR6fyahbW1kHo/sTfJtzeuD9p/r2uqqpqv7wddIETIECAAAECBAgQIECAAAECBPJNoKam5pymEjw33HTTEfnWl63xFt38q9suaKpPaV73kTz1ETYBAgQIECBAgAABAgQIECBAIL8Ebr/99j5NJXZiltGM6Ek+zsJ6ZwAuvfSKkZs2bWqqa+lc96X8Gm3REiBAgAABAgQIECBAgAABAgTyVKCuru7KprI6v/71747M0y69E/app566d9wOud2D6pvqawfWfTeOzdtEXz6Pr9gJECBAgAABAgQIECBAgACBLiTwwAMP7NhUAmfjxo2LgqE4nylGjhzZd/acOS821b80rrst6sprp3weY7ETIECAAAECBAgQIECAAAECXUQgEjA3NZXQueeee44tAILuf3/2uSTJlMnl8ai8ZwFY6QIBAgQIECBAgAABAgQIECBAIDcFxo8f/56msju1tbXlpaWl3XMz6jZFVfTb396Z6Ye7Tw3DHdoUlZ0JECBAgAABAgQIECBAgAABAgRSF4jky5+aSmLdd999J6deS27veVVZ2SfjmV9NdTNd65ZFRbvntoLoCBAgQIAAAQIECBAgQIAAAQJ5KvDyyy8f0FQWZ/PmzetKSkp65Gm3tgu79P/9v/3XVFZWNtXXNK2rjnreu13DVhAgQIAAAQIECBAgQIAAAQIECHRcIBIvzzWVxHlw/Pgvdrz23KkhEnL9FyxYML2pvqZx3fDc6bFICBAgQIAAAQIECBAgQIAAAQIFIjB9+qzDmkrgxCys6uHDhxfUQ8qTWWWTJk26v6n+pnHdqAI5NXSDAAECBAgQIECAAAECBAgQIJAzAkWRvJnTVALnkccfH5szUaYvkKI//en+a5rqbxrXnZW+cNVEgAABAgQIECBAgAABAgQIECDQbfHixZ9sJnlTE28k7FWIRD/96X9/qZk+p2v1lVFRUSHa6RMBAgQIECBAgAABAgQIECBAIOsCZWVlxZFsqWgqc/O3iRO/lvWAstTgBRdc9KGamppMvqLw52FanKXuaIYAAQIECBAgQIAAAQIECBAgUNgCq1ev/kJTCaxYVxfPjupTqL0/obR0QPS9vJm+p2P1vVFJ90L10y8CBAgQIECAAAECBAgQIECAQNYEbr755p5JsqqpjM3f//7sJVkLpBMaGjbsuH7z5s2f2lTf07TuyainRyd0TZMECBAgQIAAAQIECBAgQIAAgcISiNvpLmwuYTN69OgdCqu32/YmeUPh5MkvPdhc/9OwfnLU0XvbVv0iQIAAAQIECBAgQIAAAQIECBBok8CDDz64Q3OJmn9MmnRlmyrLz52LH3po/I+bM0jD+hlRR0EnAvNz2EVNgAABAgQIECBAgAABAgQI5JVAXV3d9c0las4444x+edWZ9gVbdPPNt57TnEEa1i+JOnZqX2iOIkCAAAECBAgQIECAAAECBAgQ6Pboo4/u1lyS5qVXXvl+VyH63veu/tTmzZubo+jo+uSNj3t0FUv9JECAAAECBAgQIECAAAECBAikXSCSK+Oay9CUlpb2T3uDOVrh2LHnHVRdXV3TnEUH11fF8fvkaNeFRYAAAQIECBAgQIAAAQIECBDIbYGXX375gOaSM1OnTr0mt6NPb3Sf+9zndl+5cuWy5jw6uD5Jjg1Kb8RqI0CAAAECBAgQIECAAAECBAh0DYGiSKwkb81rcjn77LN37BoM/9fL4cOH7zB37rypTWJ0fGVdVHFwV/LUVwIECBAgQIAAAQIECBAgQIBAWgRmzpz5weZyM2++OeOHaWkkjyqJJFbPV6e8/mhzJmlYPzSPOIRKgAABAgQIECBAgAABAgTaJVBUVlZWPG7cuO5xIV3UrhocRKCBQHIeRVnUXGLmwgsv7Ipv0it+csJT/9ucSRrWf7TBEPhKgAABAgQIECBAgAABAgTyW2DKlCn94mJ5VJQfR5nVxIVzcltScsvTjVG+HOWIKDtGkdzK76HPavTLli07Ps6ZJpeYofWTrAaTO40V3XPvvd9qEiU9K4/Ona6KhAABAgQIECBAgAABAgQItEMgro97RbmmA9fJyZvPfhllRJS+7QjBIV1IYOuMvurmzrdzzz135y7EsU1X//u/fz6mOZc0rD92m8b8IECAAAECBAgQIECAAAEC+SIQF8UHR6lMw8VxwypeiB/Hxmya3vniIM7sClRWVn6l4QnT8Ptbb3XZWVjvDMJlV1xxdF1dMtkxI8vx2R1prREgQIAAAQIECBAgQIAAgQ4KxOXxWRm5RN620tvj54FR3GbYwfEqpMNvv/32PtueJtv+6qLPwnp3iMeOPff9GzduzFQW64R3G/KFAAECBAgQIECAAAECBAjkqkCSTIpStm3KIOO/kmdnHRmlOFddxJVdgU2bNjV72+qbb7754+xGk3utnXrqqXtXrlu3LkN/mSflXo9FRIAAAQIECBAgQIAAAQIEtgrExXCSvPphhi6KU6l2WexUkrzd0KB0bYGHHnpo15ZOmNNP75JvJNzmpCgpKdllxYqVzb61sSW/FLZ9bpvG/CBAgAABAgQIECBAgAABArkiEBe1F6dwYZuNXRZEI8OjuLUwV06OTogjxv/e5k62adOmXd8JIeVckyNHjuw7f/6CZAZjJpYv5FyHBUSAAAECBAgQIECAAAECXVsgrn4/nokr4A7WOTGO36drj0zX7f3zr7wyqKXz5+yzz96x6+r8s+fDhw/vOX36G8nfSiaWU//Zkm8ECBAgQIAAAQIECBAgQKATBeKqd5comXoodDouqq+PSnp1IpGmO0cguaX11eZOoNemTv1h54SVk612f+HFF//UnFUH15+Skz0WFAECBAgQIECAAAECBAh0KYEkSfBkBy9ws3F4VTTyqShuK+xCp+fChQs/2tLJZRbWNidD8d+efPKWlrw6sO3kbVrygwABAgQIECBAgAABAgQIZFMgLmgP7cBFbWcc+ng0ulM2jbTVeQLJA/1jvMubO9FeeWXKtZ0XXU62XPTnP//l2ua8Orj+xJzssaAIECBAgAABAgQIECBAoPAF4oL2wQ5e1HbW4aXRsNlYhX+KdqusrPxcSydZaWlp/y7A0JYuFv32zrsuacmsA9s+3ZZA7EuAAAECBAgQIECAAAECBDosMGHChB4duJDNhUOfiSB27jCECnJa4Oabb+4Z49zsM9pefvnl7+Z0BzopuJ/fdNNXMvRHenQndUmzBAgQIECAAAECBAgQINAVBeLidt/2XODOmTt3zvnnf+OoQz7+8R2TN6ANGTKk14gRI3b61389c8iNv/jF116ZMmVWe+pt5zFJYmNUFLOxCvgkrqura3FG0ejRo3co4O63u2vXXfejU9v5d9XaYSPbHZQDCRAgQIAAAQIECBAgQIBAWwTiCjV5KHqblimvvTY9SVq11s4RRxyxy9VX/+D0SHYta1MD7d/5/ji0T2tx2Z6fAg888MCOLZ0a//jHpMvzs2eZj/ryK793zObNm1via++2IzIfvRYIECBAgAABAgQIECBAoMsLxFVri7NamrqqPfnk0qFthCs6/ayzDn3++Rey8abDyoj50DbGZ/c8EYixva2pc7J+3dixYyUwmxnLiy66dGTMYqunSufn+5tp0moCBAgQIECAAAECBAgQIJAegbiKvaMtV7Jvv/32lmi5e3tbLxk1auD9f/7L/RmaDdKwK1fEj+L2xum43BR44YUXBjYc5Mbfn//HPy7MzchzI6rzzrvw8Nra2sZsHf2dZMUG5UYPRUGAAAECBAgQIECAAAECBSkQF56vteXq9Ve/+vWL6YD4lxNOOCCSDRPb0nY79n0hjvF2unQMWA7VEWOajGtzS92oUaN651C4ORfKl88++5CNGzemeypWTQzI3jnXWQERIECAAAECBAgQIECAQGEIxEXnuuYyAU2tv+DCb16Txp4XnXnmOcPjGVmLmmorTeuqox7P6UnjoHV2VfPmzftQS+fGM888d15nx5jr7Y8ZM+bADRs2pDuJlfxbsluu9118BAgQIECAAAECBAgQIJB/AkUtJQKa2nbMcaNOz0A3u199zTVfiQvqpppM17pLoyJvKczA4GW7yrKysuIYy5ZeDFBz7rnntvqSgWzHnWvtlZaW7h9/c8nMqXQuK6KyHXOtr+IhQIAAAQIECBAgQIAAgTwWiAvNNiewDh32wWMy1eV4s+HOzzz77MPpvJpuVNf4+O32skwNYBbrraysPLnR2G7z86mnnspEojWLPcxOUyeWlu67fn1VMksxncu8qMzD9LMzhFohQIAAAQIECBAgQIBA4QvERWabE1h7DRw4IsMyRRddeum/RIIi3RfV9RfoycydfTLcB9VnWODmm2/uGePY0i1wVSUlJT0yHEZBVH/qqafuvX79+nT/vb0a42MWXEGcITpBgAABAgQIECBAgACBThaIC8w2JbDiwc9b3vOefbPyPKmRI0f2nTDh6fsjxkwtGZtJ1snD2mWar6ur+1ZLJ8djTzxxSpfB6GBHTzihdMDadevSncR6PMbHm0A7ODYOJ0CAAAECBAgQIECAAIEQaCkB0HhbVVXVll33HDgsi3BF3ym75oQMvDGtvmtXxBfPxcrigKazqfvvv3+X+oFs5rMinvPUPZ1tFnJdJ5xwwoCY+VjVjGV7V/8uDvQ3Vsgnjr4RIECAAAECBAgQIEAgGwJxcZk8dDmlJZ6Vk+0E1jsEcSvYLm/NmJHckpSJ5YHJbnXKxqmWkTbihPhDSyfFuPvu+3RGGi7QSpMkVgZmYqXzzaUFKq9bBAgQIECAAAECBAgQINCiQFz8v9BSAqDxtr333u/DLVaYuY3Fv7/77u82jidNv2eUl5fvlLnQ1ZwpgSlTpry3pXOgtrZ2WbTtNrY2DMBJJ520TySr0/12wrPaEIJdCRAgQIAAAQIECBAgQIDAtgJx8X9nSwmAxtsOPnjIUdvWkN1f3/72t4dv2rQp3RfXSTfXRRmU3d5oLQ0CyXPcpicD2Nxy222/7dRzNg19zHoV8WD3gVVVG9L9d1aS9Y5okAABAgQIECBAgAABAgQKQyAu+suau/Bvav2HP/bxUZ3d82NLS3detmz5m03Fl4Z1JZ3dP+23TWD58uWfbGncN2zYMCdq9BymtrF2O/XUMQeEXUtvemyJvblt721jGHYnQIAAAQIECBAgQIAAAQLvPMT99OauNJtaf8xxo07PBbd4LlaPyS+/Mq6pGNOwbmwu9FEMqQmMGzeue4x5iw8f/8mNN34wtdrs1VDg858fc1B1dVpfTpiM0y4N2/CdAAECBAgQIECAAAECBAi0KhAXk0e1JeFz6hdOu6TVSrO3Q9Hdd9/zrbbE34Z9k+dtmbWTvbHsUEvxrKtzWhrb1atXvxoNGM92KJ9++tihcdtuS7xt3TZn2rRpvdoRikMIECBAgAABAgQIECBAoKsKxJVniw/Bbnxl+vV//+ZNuWZ12RVXnJjmC+z6bt8aXzwAPNcGvIl4Hnxw8g71g9bc5zXXXHNQE4dalYLAv51//kfq6tJ6N+H4srIyf1sp2NuFAAECBAgQIECAAAECBEIgLvZ3au6Cv6n1//WTnz2Ri3CjR39+yIoVK9N6hb21/+Pjs0cu9llM2wrEOP1vU+cUsACvAABAAElEQVRs/bqly5bl5Lm7bS9y99c3L7302HrLNH1ek7u9FRkBAgQIECBAgAABAgQI5JRAXIgmzw9Kebn3j/evy6kONAjmsMNG7PX61KkVKXcm9R1fjV37NGjK1xwUmDlz5sDWhvSb37xq3xwMPW9C+t73ri5tzbiN24/Pm84LlAABAgQIECBAgAABAgQ6VyAuOJeletE5efJLWyLa7p0bcfOtv2fIkP5/f+bZWan2pw37LVq1atXOzbdsSy4IxHi+3NKYzpk79w+5EGc+x/Bf//XTr7Zk3I5tkor5fEKInQABAgQIECBAgAABAtkSiAvOW1K96Izb9Lbstdde/bIVW3vaGTJkSK8//+XB51LtUxv2q4x992hPTI7JjsCaqqqPtTaeF1xwwe7ZiaZwW7n9jt+Vtebchu0rYt/ehaulZwQIECBAgAABAgQIECCQFoG4ePxyGy42t+x3yCH7pKXhzFbS/Zbbbru/Lf1Kcd+q2C8f+p9Z3RytPcamOMq6lsbytddfvzFHw8+nsIoeemh8yonvlsZj67aH49NbIvPpDBArAQIECBAgQIAAAQIEsi0QF46HpXCB+e4un/3sKcOzHWM72yu+9ofXt/hg73c71bYvNbH7oHbG5LAMC8TYnNXacJaWlvbPcBhdofriZ599Pkk8pWv5WldA00cCBAgQIECAAAECBAgQaKdAXH226U2E377yu2Pb2VRnHFb0jW9eck1tbW26LrLr60neePi+zuiQNlsWmDx58g71g9Tc5zPPPXdpy7XYmopASUlJj6lTpyUvOUjXclAq7dqHAAECBAgQIECAAAECBLqgQFlZWXLbVcrL735/1x15xlR0+ulnXlJVldz9l9YlSWINzjOLLhFujEtrt7fVxSysXl0CI8OdHDlyZN/FixcvSdNfVnnUY1wyPGaqJ0CAAAECBAgQIECAQN4KxEXjo6legL465bWKfOzo8SecfE5FRUWq3Ux1v7rq6mqzRnLshIiEyv6tDeD48Y+dnmNh5204I48/frd169dXt2ae4va7AsLzsPL2bBA4AQIECBAgQIAAAQIEMigQF5atPjeo/uIzEjZbunUb3jOD4WSs6o9/8lOnJW9STPOSzMQalLGgVdwegaIYk6ktjfPmzZvXxSys7u2p3DHbC4wZM/agNN6qe8z2LVhDgAABAgQIECBAgAABAl1eIC70P9DSxX7jbccdd9ye+Yo28qiSzyxfvqJxlzr6uyZuUdw3X00KMe4NGzZ8srVBvWvcuE8XYt87q0+XXH75p1szT3F78qIED9rvrIHULgECBAgQIECAAAECBHJVIC4WW33wdcMLz8svv/JTudqXVOIacVTJUcuWLW/YpXR8r167dm3eJvZSccunfcaNG9c9BrXF29o2bdq0KPrkdrU0Duytt/760nT8MUUdyRsOjU0ax0ZVBAgQIECAAAECBAgQKAiBuFhMeVrSuD/ed32+d3rkyE98dOmyZWm61n63mspVq1btnO82hRJ/jMo33h2ZZr784uabP1Qo/c2RfhQ9/fdn72qGu62r/yVH+iQMAgQIECBAgAABAgQIEMgVgbiyvDbVq8v58xesyJW4OxLHiBFHDV+ydGmq3U51v0ULFy7s25G4HJsegVdeeWWX1gZtzZo1k6M1M33SQ/5OLSUlJT3mzZs/vTX7FLYnM+j6pDE0VREgQIAAAQIECBAgQIBAvgvEheLIFC4o391l4MCRBZGk+ehHjzp88ZIl7/YrTV+SB4jn5YPu8/08bhx/jMMDrY3pVVdddUDj4/zumMCIEaN2Wp+eNxPe1LFIHE2AAAECBAgQIECAAAECBSVQXl6+U2sX+g23n3322YcUCsCHP3zkYYsXL27YvXR8fzJ5DlOhGOVrP+K5ZK2+oGDu3Pl/yNf+5XLc559/fqv2Kf6hHZjL/RQbAQIECBAgQIAAAQIECGRXoCguJitSvKDccscdv/t6dsPLbGsf+tjHPrBo0aJUu5/qfr+LHd2eltmha7H2xD9Kq893GzPma7u2WJGN7RL49R13nJPqH0sL+72ajGO7AnAQAQIECBAgQIAAAQIECBSeQFwktuE5WPPfLDSBJIm1fHmruY4WrrOb3PQTF9+de6aE/2lNjkyDlc+98MJ/dG6UBdt60ZTXX3+0AXV7v36iYIV0jAABAgQIECBAgAABAgTaJhBXlsPbcnUZD2suuAcsl5Qcd1hlZWVbGFLZ9zsxEmaQtO10TNvec+fO7ZPKIBXi+Zw2xA5UNHz48B3iVs6aVMaghX3KY5tbcjswDg4lQIAAAQIECBAgQIBAwQgkb89r4QJyu02XXnnlsILpfIOOnHJK6RHV1ckL0NK6XNygiZS+RuvJ7W87RDkwyrFRktuxro9yb5SnoyS3Vr0W5ckot0RJtg+ZOXNm75Qa6EI7hcsvo7S43HPPH8/sQiRZ7eoF/37xMS3ip7bxjKwGrTECBAgQIECAAAECBAgQyF2BuI58M7VryS1bHnnkse/nbk86FtlZZ507oq6uLlWKVPe7qLWotj5M/zNR4V1ROjJr5cE4/oNRiltrsyts37BhwwFh0eJSU1NT1a201CyfzJwQRQ+Nf+ThFgeg9Y3JH6TkbGbGR60ECBAgQIAAAQIECBDIL4G4QPy31q8j/2+PtevWVUTvCvbWuIsuvbQkVYtU94uk2JWNzeLYoo0bNyZvbEtmUqV7Se6HPCNKV7/wT2azzWoN96c/vfHo/PqLzZ9oBw8esdOKFStbG4LWtn81f3osUgIECBAgQIAAAQIECBDImMDixYv3b+0KsuH2MWPGFPTb26677kcnNexvOr6vX7/hllGjLux922237Ri3Ko6OOueko95W6khmr1wQpWfGTp4crzj6PqoVoy0xA25GdKNgk7KdPURnfeXc81obg1a210ybNq1XZ/dD+wQIECBAgAABAgQIECDQyQJlZWXFcQGZ8r1zv/rV7Sd0csgZb/6WW29PZjAVyrIuOnJkxtFysIEk8ZHKIF522WUH5WD4hRJS9ycnTFySyji0sM+XCwVDPwgQIECAAAECBAgQIECgAwJx4fi/LVw8brNp3rx5T3Sgqbw59I9/vO/ibTqe/z9uiy50uZks0eeftDZ0b82Y+WDenJh5GOjwjx01ooMvSUhui/VstzwceyETIECAAAECBAgQIEAgrQKRlBrZ2kV+w+0lJSV90hpAblZW9NRTT/24Yb8L4HtF9OGA3OTOTFRVVVUDUxm30tLS3TITgVpDoOgnP/3Zy6mMQwv7DCVJgAABAgQIECBAgAABAl1c4IYb/ufAFi4ct9tUVvaDT3YRsqJZc+b8YTuA/F9xVBcZv6SbycPcZ7Q2ZBMmPFWwb9jMhbE+8MAPHFRRkeRP272YJZcLAykGAgQIFJiAh2AW2IDqDgECBAjkt0BcLhZdffXVRYceemjyv9Hdq6t37tmnz5remzZt2jG27b2pbsuRew8YUHb8ccfumGpP/zFp0uITR5/ytdriLVN2KCpaEQ+Cr45jN6d6fD7tFzNzusfD15/dcccdR+RT3CnEelZRUdFvUtgv73eJ83xUdGJ8ax057LDTek+bdm9Na/vZ3j6Ba677z6e/c8W3P9G+o985qm+cs8m/NRYCBAgQIJAWAQmstDCqhAABAgS6ikCSYIq+Js936R4leWNc7yh9o+wQJblVr/578rt/lCTRlJT63/Xrks+do+yytewenxm7LWrz5s3dVq9e3W1VRUW3ilUV3cpXVcyvWL3qmYryildWVpTPKq+oeHv1irUryssXrV0fr+GbOHFikhjIyyTX2LFj+0QSa25xcfGA6EMhLf8aCYE/FFKHmurLzJkzew8ePLjVxMedd979uTPOGPPnpuqwruMC+x544MHTXn75rZ13Tv6ZatdyVJyvz7brSAcRIECAAIEmBCSwmkCxigABAgS6nsDWxFTy0Ox+UZJkUpL8SMr+UQZHOSTK4VEylmSKunNt2bx27do3yssrpkby681VqypmL1u2eNHilSuXLlu4atW8ecvXrVgxvTqSXXUR+JZcCj7e1rjH97///cURU5JkLKTl05EUKPgH88ff489j0L7e0sDFg8aX9+3bN/kbzalzr6WY82xb0a9uve2lc75y9gfbGfc9ca5+qZ3HOowAAQIECGwnIIG1HYkVBAgQIFCoAluTVMksqSRBlTwcO3nQ8MejJLfJJL8t7Rd4I56Z89LSpUtfX7Jk+cxFi5YsXLx4/vJFixat3rhx44ZbbrmlNqrOaqLh7j/+8YNf+vznX25/l3L2yPdHYuCtnI0uDYHF3+qgqGZua1Vd86MfDfvet7/9emv72d4+gaOPPuYjEyc+8WL7jn7nqO5xrublTM4O9NmhBAgQIJAhAQmsDMGqlgABAgQ6VyAugJPb/JLb8w6M8rEop0Q5Joql8wSmxUyuFxa8/fbrixYunrFw4by3Zy1ZsmLN4sWV++yzT3XMmkr7he7kya+cM3z4Eb/qvC5npOW1UeuekRiozkjtOVDp1mRzMoMumWHV7LJs+fK/Dthrr+Oa3cGGjgoUvzVj5rqDDxqc3BrdnmWPOE/L23OgYwgQIECAQGMBCazGIn4TIECAQF4KTJ48uefw4cP3jeCTGVVnRZGsyr+RXF1ZWfncvAULXlw4f/702QsWzJk3c9HSlSsXrI7bxarvvffe5FbFti5FS5YuvTWSHGe39cAc3/+hiO+kSA5kdVZbNk0iiZXcfnZ3a21efPHFu99www2rWtvP9vYJ/OY3v73kzDPP+K/2Hd3Nc7DaCecwAgQIENheQAJrexNrCBAgQCAPBJIZGuvWrdujf//+JRHuZVE+kgdhC7EDAnV1dasXLlz49Ny58/8xa+7sqbNnzJs7Z86bS195ZV3lrFmPJA+dbzKZk7yZ8I477ngpnpeUPMOskJbRkcBKElkFuUybNq1/vI0zmW3W4jJ16tSrhg4del2LO9nYboHzz794v5tu+smCdlbwb3GO3trOYx1GgAABAgS2EZDA2obDDwIECBDIZYEkabVhw4aBkYg4M+K8PErydj8LgW61tbXd5i9YMGnO7LlPvjVrxktzZs5/c968GYvjuVxr4iHztaXnn9//zhtuWNqrV6/kIf2FsmyMjuwUCYIkeVeQS/zNPxgdO7GVzm3+8Ic/3Oell17a1Mp+NrdDIEkAjxs3LnmGXXuW/47z86L2HOgYAgQIECDQWEACq7GI3wQIECCQcwJr1qzZbaeddvp8BJbMstgj5wIUUE4LxHO3Vs+ePfvh3Xff49BBgw5o7xvVcrWPZZEguDpXg+toXDU1NR/q2bPnS63Vc/fddx83ZsyYv7a2n+3tEiiKRGJ7n0/3tzg/j21Xqw4iQIAAAQIECBAgQIBAPggks62ifCDK+CgWAgSaEVi1atXO+fA33Z4YY+ZP9+h2TTNdf3f1unVVM6J+/8dse5BbOeaqq67a913otn95ppXqbSZAgAABAikLJG9oshAgQIAAgZwRiDfRFcc10tER0NtRpkcZlTPBCYRADgrsuuuuBXuL1mmnnZY8uP/a1tj79et70CVXXbV/a/vZ3maBok+WfKrVB+m3UKtrjRZwbCJAgAABAgQIECBAIA8FImmVzLj6SJR5USwECLRBIHkLZx7+2acUcnl5+cBUKF5//fVfplShnVIWOPHEz52Vin0L+9yfcmN2JECAAAECrQj4f0VaAbKZAAECBDIvsHbt2j2jlWejvBjlgMy3qAUChSVw+OGHf6ywevTP3uy2226L4tfif65p+tthhx321ZKSkj5Nb7W2rQJ77jlw2LXXXf3rth7XaP+XG/32kwABAgQItFtAAqvddA4kQIAAgY4KbH2+zUX9+/dfFnWN7Gh9jifQVQV69OiRvJWzIJd4CPiW6Ni3U+ncOeed55bjVKBa2WeXAQMG/fBH174ybOjQVvZsdfPUVvewAwECBAgQSFHAwy5ThLIbAQIECKRXoKKiYpdYkgf8DklvzWoj0DUFJk6c2PNTn/pUbSH2fuu/FxWt9W39+qrF/fv3Gxj7JUkvSzsEdt1nn/2/c8llsy6++KJ03JZ6QCQgF7QjDIcQIECAAIHtBMzA2o7ECgIECBDItEA8L+XISF6VRzuSV5nGVn+XEXjve9+7X6F2Nh5Uvzr6NqW1/vXrt8M+l1122YGt7Wd70wKRu/r49f/xg/lpSl4ljSS3f1oIECBAgEBaBMzASgujSggQIEAgVYFIXp0V+3b0uSqpNtep+9XV1XWrra2NUtetri75/Of3dwIrKuoWsxO6FRcVv/P5zvfiom49e/Xq1rdPn25xW1inxq/x/BLYvHnz17t3735TfkWderTxb8eJsfeDrR3x2tSptx0+dOg5re1n+zYC3Yd/ZOTlt/7qf6874vBh22zowI/fxL9pyb/3FgIECBAgkBYBCay0MKqEAAECBFoTiIvP5H9zkufYXNfavjmyfW5lZeWbS5Ysm79s2bKlK8pXrFy9Zk3FmlVrKtesWbO+omL1+jVr1lavXr1mY3V1dc2amrWb1m/YsKmmorauunpF3bp1veq6d1+zuWfPnpuLi4u39OnTZ/Os3r23dJvWZ+utTS/V3+JU/1nf7ca/k/VNrdtu/7Kysh7f//73N8YGM6zrdbrW56qrr776PXEebC7Ebk+bNq3/oYceujaVvh155Gk7PP/8vRtS2ber73PAAQe897zzL/jbhRec/97+/fqlk2NwJLBmp7NCdREgQIBA1xaQwOra46/3BAgQyJpAJLCuiMZyKnm1cuXKV+bPn//w7NnzXpw3b/aspUuXLlu/fv3aW265JXmOUEtJo6y5tbWhSLaV7bnnnt9P5bgGz0wqiqRH8t8ExXEx26Nnz716DRjQq+9eex3Qf8CA5FFlu7wnZoMlzxU6IMr7oxwe5aAolhwTiGdFfT3e2vc/EVZenr+tcca/I8/FPq2+8OGuu+75/Omnf+m+1urrytv32WefPUq/OOZ/vv2tS0sH7LVXuilmRoWHRAKrIM/DdGOpjwABAgRSE5DASs3JXgQIECDQAYG46PxiHP6HDlTRoUM3btzY7Y23Zjwyferr42IWx+RXXpm/4JFHfr8uKi24mSo33HDD3hdddNHiVMCmT5++95AhQ5amsm9T+yRvkezbt2/vXr126z948IBdInE2IN4ouX/se0iUI6J8NMqeUSzZFdgUzU2P8lKUaVGSZMLCSNgunzp1auWKFSs2nHbaaXWxLu+W+LfkMxH0I60F7mHuzQoVHXHExw748hlf/MEZXz799D33fE+zO3Zww6GRvHqjg3U4nAABAgQIbCMggbUNhx8ECBAgkG6BuOBMZuu8mu56W6pv7dq1m198cdJvn332hbuefnXyy3+7//7k7WUFl6xqxqAonoX0Vlw8pjJD6pzY77Zm6knL6pjZlczq6rXffvv122OPPXYfMGDgwL322v19UXnyoJ1kJs0Ho7jlMRCyvCS3mr4Q5cUory9evHjO22+/vTiSmhU1NTXrzzvvvJychdiW2wgvv/zywT/+8Y/dwhYDvNewYf2+8ImSo08c/dmffvrYYw6JZ6XF2owtf4l/V07OWO0qJkCAAIEuKyCB1WWHXscJECCQeYFIXvWNVpK3DSafGV1mzZ699OHx48vGP/S38Y8//kDy5quukrDazjVuDRx19NFHj99uw/YrFseFZnJrYGfe5lN0880394jZW/3e97737TZw4MB94o1zgyOmJMF1ZJSPRJHgCoQsL8nfzytRnt+0adOURYsWzZy3ZMmixXPnls+cOXN9JCaTWV6dct7EvyuvRdtDo7S4vD516q+HDR36lRZ3KtyNMdOqZOdPf+aoj5446tNXHnnkkUdn6aUQ6yMR+p5IWG8oXFo9I0CAAIHOEpDA6ix57RIgQKDABeIiM/nfmIejjMpUV2PWyPrf/f7uK/7y5/vuee6551ZEO51yQZ2p/rW33gsvvLD3jTfeWJ3K8bNnz95r8ODBy1PZt5P2eSfBdeCBB+4QM7l2jwvjfeOB+AdHLB+KcnSUIZ0Ul2a7dUtuEXsuZvy9Gs9emxmJi7eXLFmyMm7ZrYyZXBsz9TD5eJvnaTGD6J5UBiASN/Ew9+e7QjKl6OMfP6n/kUcfdtAnPnbkZ0aM+Og34/bAPVIxSvM+h0VSPLlt1UKAAAECBAgQIECAAIH8EIgE1nFR0r7ExfKWCROeeuHLZ52VJDDMzGnmdIiEwi0p4n+tmSryYnX0sWjChAl9ps+bt3fc+jYifl8Q5dEU+263zApURvWPR/lhlDPjAfNHR8L0oBivPW6PMUtuL23PSZYcn2rYd9xx5xfa00aOH1N80kln7/id73znA3/4w72lr7/++q1x7lelapLB/U7JcTfhESBAgECeC5iBlecDKHwCBAjkokDcYtQ7ZvWsidh6pzO+p595ZsL3v3vt2RMnPjovnfUWYl0xA+t9MRNrVgp9WxX77BGzJgpq9lqSHNn3wAMP+PDQw392xBHDTkrBwS6dI5DMjpocZUqUGVHmR1laWVm5qry8fG3MsqyKsjFmdNXWz+iKBEzy368ro+wWpcVl3fr1S3fs3z+5TTYfHlof/SrpXlLSrUe/fv367rnn/2fvTuCjKM8HjueEcAcMN0IAsQL1DKKo1SCeVLFWo1aLYr2pVESUam2N+rdavFr8UI964xk8iijeBhEjQqCIgtwJIAkhhEBIQk7yf15MIITsZuedY2dmf/v5vGZ3dt73fZ7vO4k7LzPv9mvfrc8hif0OPbT7gL7Jyf0HJB/Vr2/fU1u3bq3WFXTb43r5G/IftwVFPAgggAAC/hJgAstf40k2CCCAgCsE5ARzigTykFXBrF67dsvd99xz7szXXnN0MXir4g9TO2ox9y1yUtnitwDKbV/9evXqtTFMcdrdbbQcj99KJ2otLR7eFlDrbq2sLyPlZ0i3yH32+RdR63Nyt1RWVHxZUVWVXV6+e+Xuqt0bKnfVbisvry1v166qSu5+rJW11+rkKrF9a+fNHDq0Lipdeklbvvfzcpo8lW9wjJYviYiuqKiILisri+nZs+fen/JtnLHx8fGx8jsXX5eQEN+pVSt52S4hoUPrhA4JHeTthPbyn/Zt27ZL7JjYrktiYpdDEhM7dZfSO7Fjp+TEzonJXZOSWnfs2FF68dzjUvk7k+G5qAkYAQQQQAABBBBAAAEEIltAviWslZW3qLz+5psPi2hcZKvqZZ+ZOe+yEMciXa8Hb9QSg0tCdGA3BBAwJnCiN/4KECUCCCCAgB8EtNYe8EPi5IAAAgggYI/AkCFDjrWiZfnms6hbbpl07u8uvfR2aa/GijYjrY3nn//P7BBzvisjIyM2xH09t9umTZsWey5oAkbA3QJr5Eq0rnLl1QJ3h0l0CCCAAAJ+EmACy0+jSS4IIICAOwRONxuG3IYTNX78LcOmTXv8I7NtRXL9GTNmlG3btu3VEAziR48ePSSE/Ty5iywcvsGTgRM0Au4UeETCGiy3O6p1yHgggAACCCDgmAATWI5R0xECCCAQMQLq2wFNPaZMues3zz77JFfNmFL8ufJrr719byjNyKLRat0yXz5GjhypruB705fJkRQCzgnkSFeD5Kqr26V4YVF852ToCQEEEEDAEYG9i1I60hOdIIAAAghEhIAsnzJfEj1ZN9mPP/nsv+ecfeZvpb6vvhVP18OCemox9zw54ezRUlsffvhhglyJVdnSfl58v7Ky8petWrX63ouxEzMCYRZQC9tfI+Vl+Tuyb5H7MMdE9wgggAACESjAFVgROOikjAACCLhVQK179Ze7HrhO4mPyyrpBqvvqq6w/htLc6aeffmoo+3lxnwcffHCFxL3di7ETMwJhFEiXvtvJxNWLTF6FcRToGgEEEEBgrwATWBwICCCAAAJWC5TrNvjOrFnvLF48l3VVdAED1HvhhWfnBHjrgM2tW7f+xwEbfPQiPT1dXTlytY9SIhUE7BT4P2m8k0xa3Sulws6OaBsBBBBAAIFQBZjAClWK/RBAAAEEQhXQnoD6et4394XaCfuFLvDiiy9WFBUV/SuEGscuX768Swj7eXKXuXPnqom8Mk8GT9AI2C+wW7q4Lj8/X11x9VcpJfZ3SQ8IIIAAAgiELsAEVuhW7IkAAgggEJrA5tB2O3iv2tqKdQdvZYsVAi+/PPPhUNoZMmTIJaHs58V96hdzv8yLsQeLefXqNVHPv/ji1V999fV1+QUFj8u+q4Ltz3sINBH4WF4fP3PmzA4yafVsr169tK+ibdIuLxFAAAEEELBUgAksSzlpDAEEEEBABH5CwX0CkybdtFkW2P9fCJE9KLfb+fnzgboKa1MIDp7Z5fDDB0Vt37Zt1amnnvJsrx49JskkxBH33ntv7A033JD46KOPDp41a9aYZT/8cG9pWdmXkhSLcHtmZG0NVP0tGFNYWKgmrc6Rkn3JJZfwzYK2ktM4AggggIBZAb6F0Kwg9RFAAAEEDhCQSZLzZMPsAzaG+OLJJ58/Yvz4a7h6JEQvo7t9NnfuiFGnnZbVUr1du3b9omPHjqtb2s+r78sxeoTE/qNX4w8Q955rr53U9bnnHm9pofrotLS0+G7dunU4bMiQbgMP7Z/cv/+hQ5OTk09o3759qrSdFKB9Nntf4HVJ4ekVK1YsHjp0aKn30yEDBBBAAIFIE2ACK9JGnHwRQAABmwVkcmCodPGDTjeLF//vr8OGHacWD+Zhg4BMXMRmZGTskqbbtND8c3JFxrUt7OPpt+U4fUYSUN946ZvH7t27Nxx//FWHL18+s0o3KXX1XV5eXkK/fv06yaRW9/79+yf36dNniNxWlhIbG3uKtNtNt23qOSqgrrR7R8qb27Zty0pKStoiv9NcfefoENAZAggggIDVAkxgWS1KewgggECEC8jEQCch2KHDIHV3x8TEtJe6nGjpAIZQZ9WqNdcefvhh/2lp1w8//DBh9OjRlS3t59X35VhrJbHnS/HVovWbN2+eIxNO50tetvwOqQmu3KioVoe3atVBJrgO6dGnT+8+PXoc1rt376Ft27YdLv0eJyVeCg/nBKqlq0+lzK6url4ox8B6GZudMmFV51wI9IQAAggggID9Akxg2W9MDwgggEBECcjEgPp/S40UrXWU7v/HP47625///H1EoTmY7OTJk9s9/PDDodw+dLacAH/iYGiOdyXH6uHSqe9uWf120aL7Txw+/G+Og9Z3qK70kyu4EmRSq2PX3r2TDu3evafcsti/R48egxMTE4+S3YZJ6RCu+Dzcr7rtNVNKtpSVcsXdxoKCgiK5Sk5NNDNZJQg8EEAAAQT8LcAElr/Hl+wQQACBsAjIxMAL0vE4nc7lK9w/ltuVztGpS53QBLZv3/5U586db2hh7+UygfXLFvbx/NtyrF4lSbzo+USaJPDam29edMVll6lbyFz5UFdyFRUVxcfGHtKmR//OHbt37NhFbnPrlpjYpXeXLol9O3fpMqBzYuIvEhIShkgCfp7sUpNPar05NZGaU182yM8CKdt27NixUyb9yuR5DVdUiQIPBBBAAIGIFmACK6KHn+QRQAABewRkUuBkaXm+buu3335790ceeWSrbn3qBReYNm1anwkTJrT4TXxbt27t0b17d3Ui7edHtByv/5IEJ/gtyb/97W+/vP/++5f7IK+9C89LHm06d+7VqeMhbXsmdUkadEhi4nCZ6BrdpUvngV26dIkaOmRwVFxcnF3pqlsyt0nZO7FU/1zdKr1TirqiUa0tV1L/vFx+qqImnlRRzyvqS2Vubm61lJrU1NRaJqVEhQcCCCCAAAIhCjCBFSIUuyGAAAIIhC4gEwIJsvfu0GscuOf6nJxXBw4Y8PsDt/LKSoGqqqrs+Pj4lBbavEtOsB9sYR/Pvy3Hq/o89LQUXy3qvmfPnurf/P73vWa//rqaePHzIy6xZ8/eGS/PyDzzjFH9Q030hZdmvL/qxx8zY+Jj17aOS9gSG1tXLGvwlcpaXrt3tm1b1TMqqlquVNwjt0TuYaIpVFX2QwABBBBAAAEEEEAAAQQ8JiCTAm9I0X785S9/OdRjKXsq3LfeemtYCINTJd9aGOupxDSDFQt1JdbEEEw8tYvcLpovV/qoCWXfP+TKzWOMDI66jdH3KCSIAAIIIIAAAggggAACCCAQXEBOJIcYOZlsuq+sj7NAeuBK4eDM+u/KybuYFzV1b+a1+ma5iHlI/sOlVDTj4NlNq1avXhglC6tHwCCqY7ok1IGSvzF9IsCEFBFAAAEEEEAAAQQQQAABBIIJyEmkuqKlINSTyeb2e/bZF0cF64P3zAlkZWWlNefeZNs8c714r7as/dVeDD5o4uDpl4sXL3lDRsL3E8JLly6dZGCgbvbe0UnECCCAAAIIIIAAAggggAAClgvIieT5Bk4mD9pV1vCpiJTbnyzHD6FBWdun1UHozWyQb0LrHEJzvtpFGNQE7ElSCpshsX1TdXW15X18npn5kAySryexHnroob4G4NTY+trDV7+UJIMAAggggAACCCCAAAII2CUgJ4dxUkzdjrVq1aqX7IqPdqOicnM33hXCCb/vvqEv1LEXm1gpF0sJ+da0EDwD7ZJVUFBwxqRJfz72mJThP2zevDnQftrb33777RtCzd2L+6l1rQSnNlSgwsLCXl7Mk5gRQAABBBBAAAEEEEAAAQQsFpATybGhnkwG2u/xxx8fYXFYNFcvMDE9PTGQe6Pt5fI8ohe8lvzVRNavpGRLsfKRK41dKusxdWxyUMb86rRRd+3cudPKvva29eyzL5zTpC9fvSwoKHzGANokXyVPMggggAACCCCAAAIIIIAAAnoC2dnZ8XIyaeoqrJqamvLzzz+/rV4E1GpJYNeuXa+1dMJfVVV1XEvtRMr7YtVZiroqa35LbgHeV4vnq2887Ckl6C1sF16YdorYB2hGf/MDU6ce49fxysh45zQDMurKuqBj4Fcn8kIAAQQQQAABBBBAAAEEEGgiICeIlxk4oWx2140bf/pEmuVEs4mtFS+ffvrpUNYN+syKvvzWRv0EbS85aEdK+aOUp6TMkbJQygIps6X8U8o4KSnr1q3rJD8NHcfXTpjQR+pYfgvj7bffPtBv46HyGTdxYihXFQrpzw+5VfNQPzqQEwIIIIAAAggggAACCCCAgEEBOU1Ut1+ZXgx75sy3rzXYNbuHKCCLhqsJl6CPZcuWRdxi7iHy2b6brO3UUQZnfdABMvimfElC1ZVXXtnb9uCd70AtwK9uew31cZPzIdIjAggggAACCCCAAAIIIICAKwXkTHJEqGeTwfa79dZbD3Nlgh4Pavbs2ccFc69/7xaPp+np8OfMmdNaxsHSNbgqKirKL7zwykM8DdNM8N9/v/zJEI7nhl02NtMEmxBAAAEEEEAAAQQQQAABBCJRQM4U1VUR6pYqUw854S5OTU1NiERDm3NW45PfwuBUZGRkxNocB80HEcjMzFTf7PlRC+Nk6O2ysvLitLS0TkG69dxb06ZPP8sIwoIFC5ouou+5nAkYAQQQQAABBBBAAAEEEEDAIoHS0tIeRk4qA+27PidnroQU0d+KZ9GQHNDM4u++OyeQecN2WVD8+AMq8cJxARmLGClvNIyJFT/Ly8sLz73iCt9M4siEnKG/NXIL7SmODyQdIoAAAggggAACCCCAAAIIuFdATrbvtOKEe86cj+5zb5bejCw1PV1d3VPRwvjM92Z2/opa1sRSk1hPtTBWht7evXt3wZgxf+jgD6m02NraWiP5P+GPvMkCAQQQQAABBBBAAAEEEEDAEgE5o1STJKYXdFdnplMfe+wsS4KikX0C8o1s1yjbYI+8vLykfRV4EjYBGSN12+ejwcbK6HtyJVa+XL3UPmxJWdjxihU//mAgfzVxa+jbIS0MlaYQQAABBBBAAAEEEEAAAQTcKCAnikMNnFgG3FW+Ra3ummuu6efGHL0ak1zZkxAQfP8bd3s1P7/FLUOiJrHu3z805p/JlVj5frgS68WXXzF0teeaNWt8cwul345z8kEAAQQQQAABBBBAAAEEwiYgp9mWXDmya1dpiSzq7osrRsI2GE063rlr130tTIPUqsXEm1TjZZgEZKzUJNbdLYyZobflyxIKf/ObcYlhSsmSbm++eeLJhpKuqxtmScc0ggACCCCAAAIIIIAAAggg4B+B7OzseDm5tORWwo0bN/0gk1hMqFh0eDz++OOJIZz4j7KoO5qxQEDGS01i3RHCuIW8iyzYX3zllVceYkF4YWkiJTU1KeRkf97xtrAESqcIIIAAAggggAACCCCAAALuFpBzxkEGTzAD7r548f/elWxZw8aiIa+srHwhIPbPb6zE2yJsC5uRoZnUwrgZeluOg/Jx48b1sDBEJ5uKK9m1y0i+85wMjr4QQAABBBBAAAEEEEAAAQQ8JCBnl7cZOcMMtu/77895wEOpuzrUGTNm9Axmrd6TtZL6ujqJCA1OhuaWlsbOyPs1NTUVEydOTPYiZ9Y3CzYbyVV9u6MX8yRmBBBAAAEEEEAAAQQQQAABmwXk5DJGSraRk8xg+06f/uQ4m0OOmOZlkfzPglnLe89EDIbHEpWxuamFsTP89n0PPnisxxii/vPcc9MMJtrBazkSLwIIIIAAAggggAACCCCAgEMCcoLZSUqVwRPNgLvffvudpzkUuq+7mT17dou3eMpaZm19jeDh5OQX5NqAvySabzzz/PNne4lkwsTbLjaY6hAv5UesCCCAAAIIIIAAAggggAACDgvISeYIgyeaAXevra2tu/K66w53OAU/dqcWBl8aEPrnN8b6MXG/5CRDdFUL42f47Xffe2+cV3zOOOPXgw0meLlXciNOBBBAAAEEEEAAAQQQQACBMAnIieZ9Bk82A+4ui09XjR6d5tXFp8M0Agd3+/nnnx8XEPnnN4pZN+hgNzdtkWFKa2EMDb/95Vdf3SM5uv5LE5KTkxPlVlgj+T3hprEjFgQQQAABBBBAAAEEEEAAARcKyFmmWg9roZGzzWD77tixo+SEE87t6MJUvRSSugprYzDnqqqq47yUUCTGKuN3brAx1Hnv++XL30hLS4t1uWfc5s2G1nFf6vJ8CA8BBBBAAAEEEEAAAQQQQMANAnIi3U5Kqc4JdXN18vLzN44YMaKNG3LzagyLFi1Kbc620bb5Xs0tkuKW8fpVozGz5OmGDRuz3f77NT/rG6N/T1x/ZVkkHbfkigACCCCAAAIIIIAAAgi4VkDOrFtcPNzI2fe6det+SElJiXdtwi4PTN0iKN7FwcwLCgq6uzwNwhMBGcNjgo2jznuFhdsKzj03ratbgV9/4405BvNq7dZciAsBBBBAAAEEEEAAAQQQQMBlAnLCeYnBk86guy9fvlyuEnL97U4uG4X94axateo3QYHr6qbu35tnbhaQcRwgxbJv/VTHhdxGWnvHHX8Z5sa8H3jwH+kqRgOPJDfmQUwIIIAAAggggAACCCCAAALuFFBrLz1h4KSzxV0XLcr+QFKNcWe67o5KrsKKE+CKYMhr1qzhyhV3D+O+6GQcu0spCTaeOu+98vrrN0onrroF78Ybx19hMJfB+6B4ggACCCCAAAIIIIAAAggggEBLAnLSqW5dm2/w5DPo7nPnzntN+mUSqyX8Zt7ftGnTNUFx5dvumqnGJpcKyFh2khJ0gf4WxrvZtxcv/t8Lblrc/cIL005rNtDAG0e6dMgICwEEEEAAAQQQQAABBBBAwK0Cco6ZIKUg8Lmm8Xc+/PDjpyRfV10l4lb/xnFNmDCtdQvahWq9rMZ1eO5uARlP9fu1pIVxNfx2QcHWlb/+9a87uyH7U88440iDCVzhhriJAQEEEEAAAQQQQAABBBBAwGMCcvLZTUqtwZPQoLu/O2vWo8LAJJbBY6GwsOi2YLBlZWXHGmyS3cMsIOOpbg/9INi46ryn1sWaPPnOEWFOL+qYE09MNhj/XeGOmf4RQAABBBBAAAEEEEAAAQQ8KiAnoEcbPAltcfc3Z779gHAwiWXgmJArrNq2ACuL5fPwmoCMqbpdd1oLY6v19owZr/5VPMJ2ZV6/wYN7Ggz8Ca+NH/EigAACCCCAAAIIIIAAAgi4SEBOQi80eCLa4u4zXn1dnVwziWVgnHft2nVfMNicnJweBppjV5cIyJiqL064KdjY6r63aNHihSkpKZ3CkWrHPn267Nmzx0jo74YjTvpEAAEEEEAAAQQQQAABBBDwkYCchd5p5Ew0lH2fff75O4SISawQj5Np06Z1bMH1nyE2xW4uFJCxHdXC+Gq9XVJSUnv9+PEjnU65c+cBnUpLy4zEvMDpGOkPAQQQQAABBBBAAAEEEEDAZwJyFqquEnnByNloKPvKJNZkn1HZmk5FRUXQ282ysrLa2BoAjdsqIL8z/aWUhvK7Y3SfmTPfekauxoq3NYFGjXft2rV9eXm5kTBXNqrOUwQQQAABBBBAAAEEEEAAAQT0BORMVK3X85mRM9JQ9n3muecm6UUUebVeeumlQ1owHRd5Kv7KWMa3g5SVLYyz1tu5uRuKRl9wwZFOiKkJrMrKSiNx5jsRF30ggAACCCCAAAIIIIAAAghEgICcjcZL+cHIWWko+/7nuecmRgCfJSnW1NTMCGJampGREWtJRzQSNgEZX/UNhRlBxln7LbUu1XMvvDTN7quxkpJ+oSbijDwKwwZOxwgggAACCCCAAAIIIIAAAv4TkDNS9Y14BUbOTEPZ95lnuBIrlKNFJqh6BPOsrq4+NZR22MfdAjLG6rbdicHG2sx7eXl5xZeNvfoEUbBlHbojjzyys8H4uALL3Yck0SGAAAIIIIAAAggggAAC3hOQE1N1cmpogZtQTmbr18Sy5YTae8qBI5araGYF8VRrCWEYmM9T78g4nyilNsh4m3rr3VnvzTnmmGMSrUY577zzehsMbK3VMdAeAggggAACCCCAAAIIIIAAAlFycqpOUC0/sX5pxqtThJcJmCDH2H//+99Dg00O7Ny587Ag1XnLYwIy1l2krA425mbeU4utP/ro4xOHDh3ayiqaSZP+fKzBmOZb1TftIIAAAggggAACCCCAAAIIIHCAgJygDjJ4khrS7q+//uY90hGTWAdoH/hCID8JgvnugXvzyusCmZmZal2s54KMuem3thQUVNx66+QLxSrGrNdrr71xg8GA3jDbJ/URQAABBBBAAAEEEEAAAQQQCCggJ6nDDJ6ohrT722+/+3fplEmsAPLvvfde/2CQGzZs6BygKpu9K6DWxbog2Lhb8d6q1WuKZCLr/Kio1DhNquji4uK1BmNJ1+yLaggggAACCCCAAAIIIIAAAgiEJiAnqmcaPFkNafdZs2c/KhEwiRVgGGQtrHlBIO8LUI3NHheQMe8uJTfI2Fvy1rZt2ysefvixq+XWwvZGyK67bvwxGgFcbqQP9kUAAQQQQAABBBBAAAEEEEBAS0BOWH+rcdLaYpU5H330pARk+pYmraRcXmn27E+C3sIpt50luDwFwtMUkF8cdUvhYy3+AlmwQ0VFRd1b77w7+8or/zCiT58+bYKFfPzJJw+VbzjU+YKHY4K1y3sIIIAAAgggEB4B/iU5PO70igACCCBgs4CcK4+VLl62upu5X371ysjUU6+SdvdY3bbH21O3lH0rORwfII+x0dHRrwR4j80+EJDxV2P/pZSgE0tWpVpevjtq0aJFm7IWfPvmmrXrvsvP/6kkOjquVdcunQeOOOmka64c+/tBbdsaD0W+eKBLYmJisVVx0g4CCCCAAAIIIIAAAggggAACQQXkhPomKZY/vl20SC1MHhu08wh8c86cOUOCYJdmZGRg5vPjQsa/jZS3gxwHbn+rVALkH3h9fpySHgIIIIAAAggggAACCCDgOgE5GZ1sxxnzsmXLPklN1V5Y2nVOFgWkrsJaGsi7urr6VIv6oRl3C6jj4HQpVYGOBRdvn+RuWqJDAAEEEEAAAQQQQAABBBDwq4A6mb7LjhPmVatWL5BFpVv5FU4nr48++ujoINarpU2ubtGB9WAdOQ7U1VivBDke3PhWJw9SEzICCCCAAAIIIIAAAggggIAfBOQsWU1i/dWOs+Xc3A0r5EosFijff6Ao6xWBrEtKSg7fvyvPIkFAjoVjpRQGOiZctP3vkTAe5IgAAggggAACCCCAAAIIIOBiATlJVhMr99lxspy/ZUuuTGK1d3H6job2+eefHxfE+SNHg6EzVwjI8RAr5eYgx0W436qQAJiIdsXRQhAIIIAAAggggAACCCCAQIQLyAmqmsR6wI4z5eLi4sKzzz67S4QTN6SvnFcGcl6/fn33hh35GVkC27dv7yTHhRtvK0yNrJEgWwQQQAABBBBAAAEEEEAAAVcLyMmzbZNYZWXl5ZdddlkvVwM4FNwXX3yREmgCS7ZPdygMunGpwO7du5PlOMgKcow4+dYzwsTabC49VggLAQQQQAABBBBAAAEEEIhYATkzVpNY6XacIVdVVdXecMMNgyIWd3/iynh1IOPMzExuudxvFanP1DEyWMqSQMeJA9s/kT5iI3UAyBsBBBBAAAEEEEAAAQQQQMDlAnLSqk6ebVnYvba2tu622/58vMsJbA9v/vz5w4JMQEy0PQA68IqA+l0cJOWzIMeLHW/NkkaZvPLKUUKcCCCAAAIIIIAAAggggECkCsjJqzpxvtOOM2PV5t+nTj0vUm3r81a+awP41j799NPxEe5D+k0ECgoKuqtfnQDHjJWb75DGuG2wiT8vEUAAAQQQQAABBBBAAAEE3CugJllus/LMuHFbzz7/0rXuTd3+yLKysoY39mj8vKam5rf2R0APXhTIyMhoJcfKaVKsXidrmbSZ7EUTYkYAAQQQQAABBBBAAAEEEEAgSk5qb5Jiy2PW++//VYgj9WoPNUG4PgBscXp6egyHHwLBBPLy8trK8TNSivr2wtoAx1JLm+fIDkdKidTfw2DEvIcAAggggAACCCCAAAIIIOAlATm5vUqKLY+v5mc9JRYROVmzYMGCEwOhlpSUnOKlY4RYwyugJjzlmEmS42mElAlSZkhZIaVcSsMjV568LeWPUo6VkhDeqOkdAQQQQAABBBBAAAEEEEAAAYsF5GT3Yim2PL7/4YfZqampcRaH7IXmgl2FtVoS4KoYL4wiMSKAAAIIIIAAAggggAACCCCAgHsEZPZqtC0zWNLohg0bs2USK+KuCPnqq69OCGS6bdu2we4ZfSJBAAEEEEAAAQQQQAABBBBAAAEEPCIgky2/CjThYnZ7UVFR7rnnntvRIxRWhamuwgr0jYRzreqEdhBAAAEEEEAAAQQQQAABBBBAAIGIEpAJl6PMTlYFql9eXl7yhz/8oVckgWZmZg4L5LFq1arekWRBrggggAACCCCAAAIIIIAAAggggIBlAjLhMlBKVaCJFzPba+Vx65QpR1oWrPsbUldhrQ5g9pL7wydCBBBAAAEEEEAAAQQQQAABBBBAwKUCMuHSS0rjbzkLMAejt/mf/3zibJembnlY77//cUogJVknq7PlHdIgAggggAACCCCAAAIIIIAAAgggECkCMulyiJSiQJMvZre/+dZbN4plJHwbX/SePXtWBPB6IFKOJ/JEAAEEEEAAAQQQQAABBBBAAAEEbBGQSZf2UtYHmHwxvXnuvHmPSuAxtgTvokbf/eCDYwJhZWVltXFRqISCAAIIIIAAAggggAACCCCAAAIIeE8gJycnQSZflgSagDG7/ccfV85KTU2N856MoYijZfmvHwJY/dFQS+yMAAIIIIAAAggggAACCCCAAAIIIHCwgEy8xEn5LMAEjOnNeVu2LB0xYoSvr0R6661Zgb7hserpp5+OP1idLQgggAACCCCAAAIIIIAAAggggAAChgQyMjJiZabqNdOzVQEaKC0tK/jd736XZCgob+0cXV1dvbS59CsrKy/2VipEiwACCCCAAAIIIIAAAggggAACCLhUQCZfoqU83NwkjBXb5Da7qtvuvPMIl6ZvOiyZBBwSwKk4PT3d92uBmQakAQQQQAABBBBAAAEEEEAAAQQQQCBEATWJdUuAiRhLNj/x5JNnhxiL13aLrqqqWtAc0q5du0Z6LRniRQABBBBAAAEEEEAAAQQQQAABBFwtIJMwv21uIsaqbe+/P2eCAES7GkEjuFdffXVQAKONst13+WoQUQUBBBBAAAEEEEAAAQQQQAABBBCwTkAmXEYEmIyxZPPi/333jETru1vrKioq5jYHlJdXeJx1o0NLCCCAAAIIIIAAAggggAACCCCAAAJ7BWQi5jApVc1NyFix7aeffpp/7rnntvYT9wsvvJAcwGap5MlVWH4abHJBAAEEEEAAAQQQQAABBBBAAAF3CJSUlCTJhExhgEkZ05tLdu3anJZ2TRd3ZGtNFHIV1pzmYNatWzfImh5oBQEEEEAAAQQQQAABBBBAAAEEEEDgAAGZjGkrZUVzkzJWbFPfUHjHHXcPPqBTD7+YPn36oQFcPvFwWoSOAAIIIIAAAggggAACCCCAAAIIuFsgOzs7XiZlPgkwMWPJ5mefffZ8dyuEHl1ZWdnbzaEsWbKkV+itsCcCCCCAAAIIIIAAAggggAACCCCAgCEBmZCJkfJMcxMzVm377LPP7pSgPL9WlFyF1SOAyQxD6OyMAAIIIIAAAggggAACCCCAAAIIIGBMQCZloqXcEWByxpLNq1atei0tLS3WWGTu23vXrl2vNAeSlZXlqzW/3CdPRAgggAACCCCAAAIIIIAAAggggIAIyMTMb5qbnLFqW2HhtiXnn39+Wy9jpz/yiFoAv7nHY17Oi9gRQAABBBBAAAEEEEAAAQQQQAABzwjIzMzRUmqbm6GxYltVVVXxhAkT+ngGpJlAt27dOr05i8zMzPbN7M4mBBBAAAEEEEAAAQQQQAABBBBAAAGrBWRyRq31VNzcJI1V26b9+9+nWR23U+1NmTKlUwAHtdYXDwQQQAABBBBAAAEEEEAAAQQQQAABJwTkKqP2MkmzMsBEjSWbP/ro04mSiycXd9+48acHmkN44YUXEpwYH/pAAAEEEEAAAQQQQAABBBBAAAEEEBCB7OzseJmk+aC5iRqrti1f/uMrXlzcffLkye2aM5BbJG/g4EEAAQQQQAABBBBAAAEEEEAAAQQQcFAgPT09RiZqpjY3WWPVtm3bipadddbYdg6mZUlXq1evntyMQdXTTz8db0kHNIIAAggggAACCCCAAAIIIIAAAgggELqATNRc1cxkjWWb5MqlklumTOkbekTh33PcuHEJAnDQgveVlZVp4Y+OCBBAAAEEEEAAAQQQQAABBBBAAIEIFJDJmuGWzVgFaOg/zz9/updoFy1efEMzqZRmZGTEeikPYkUAAQQQQAABBBBAAAEEEEAAAQR8I1BeXt5bJmxs/YbCefPmq2/z88Ti7tdff71aJ6yi6SRWaWnFOb4ZdBJBAAEEEEAAAQQQQAABBBBAAAEEvCZQ/w2FPzSdtLHy9fr1ubNTUlI8sZbUvHlfX9ZM7gVq/TCvjS3xIoAAAggggAACCCCAAAIIIIAAAr4RyMzMjJNJm4xmJm4s21RSWpp7zTXXdHE7mvoWRUm6pGni27dvP8XtsRMfAggggAACCCCAAAIIIIAAAggg4GsBmbCJlnJX04kbK1/v2bOn6sEHH0xxO+T77394djN5r5dtnrgV0u2+xIcAAggggAACCCCAAAIIIIAAAgiYEpBJmtHNTN5Yuum99z64QYJ08WRQekxtbe3mpknL7ZbHmsKlMgIIIIAAAggggAACCCCAAAIIIICANQIVFRWDZPKmvOkEjpWvV6z48aXU1NQ4ayK2vpXXMjJGNJPvMunJxRNv1jvQIgIIIIAAAggggAACCCCAAAIIIOBaAVnzqZNM4KxtZhLHsk3FxcU/jBnzhw4uRYiurKxc0TTZnJycI1waL2EhgAACCCCAAAIIIIAAAggggAACXixk5QAAQABJREFUkSeQnZ0dLxM4s5pO4lj5uqamply+4e9wN+o++eyzv2wm1/lujJWYEEAAAQQQQAABBBBAAAEEEEAAgYgVkAkctbj7X5uZyLF008x33rlMkN12e170rl1l85smKldhJUfsAUHiCCCAAAIIIIAAAggggAACCCCAgFsFZBLnnKYTOVa/XrZs2VNpaWmxbjJ4/PHHk5vJ8wM3xUgsCCCAAAIIIIAAAggggAACCCCAAAL1ArK4+wCZzCltZkLHsk1FRUXLxowZ46p1sWQ9sINuo1yxYkVPDgwEEEAAAQQQQAABBBBAAAEEEEAAARcKFBYWdpDZqh8sm7FqpqH6dbFcs1i6rNHVo5kwX3Ph8BASAggggAACCCCAAAIIIIAAAggggIASkMmcWCkvNTOpY+mmd999b6x054p1sfLy8p5qmlz2qlVJHBEIIIAAAggggAACCCCAAAIIIIAAAi4VkMkctbj7jU0ndax+/eOPP76QmpoaF26GCRMmdGwmt6fCHRf9I4AAAggggAACCCCAAAIIIIAAAgi0ICCTOsOk1DYzuWPZph07dqz8zbhxiS2EYvvbK1asvLtpUpmZmWGPy/bE6QABBBBAAAEEEEAAAQQQQAABBBDwukBJSUmSTOxsbjq5Y+XrPXv2VD3wwNTjwmk1bty4BMmp6WTd1HDGRN8IIIAAAggggAACCCCAAAIIIIAAAiEKLK+rayWTO3OsnLRqrq05H388QUIK27pYCxYsuLppXHIVVvsQmdgNAQQQQAABBBBAAAEEEEAAAQQQQCCcAjKxo9bFOug2u6YTPmZfr8/JmTU0La1VOHJV63FJ/KVNcrg7HLHQJwIIIIAAAggggAACCCCAAAIIIICApoBM7oxsMsFj+cuysvL88ePH99AM0VS12bM/HN00oaysrDamGqUyAggggAACCCCAAAIIIIAAAggggICzAmVlZT1lkqeg6USP1a//858XznQ2s729xVRXVzdd8+uWMMRBlwgggAACCCCAAAIIIIAAAggggAACZgRycnLUoudfWD1p1bS9RYsWPSRxxpiJ1Wjd559/+YQmcdTOmTOntdF22B8BBBBAAAEEEEAAAQQQQAABBBBAIMwC6enpMTLRc1+TyR7LX27ZsnVhalqak4upR5eWli1tksi1YeamewQQQAABBBBAAAEEEEAAAQQQQAABXQGZ6BnVZLLH8pdyW1/53fffP1g3RqP1pk6dOqhJElXZ2dnxRtthfwQQQAABBBBAAAEEEEAAAQQQQAABlwg4tS7W7DkfXScpRzuR9tathbObTGJd7kS/9IEAAggggAACCCCAAAIIIIAAAgggYJPAmjVrWsuEz5wmkz6Wv1y/fv3MoUPTWtmUxr5mJ09O79Yk+PLMzMy4fTvwBAEEEEAAAQQQQAABBBBAAAEEEEDAewIy4RMtZUqTiR/LX8oVX5snTpzY026hdevW/bNJ8Bfa3SftI4AAAggggAACCCCAAAIIIIAAAgg4ICBrVp0sEz+1TSZ/LH/5yitvnGtnOmPHTm7XJOiSjIyMWDv7pG0EEEAAAQQQQAABBBBAAAEEEEAAAYcESkpKDpHJn/VNJoAsf7l02bJpaWlptk0qLVmydEKToM9xiJBuEEAAAQQQQAABBBBAAAEEEEAAAQTsFlDf3CeTP681mQCy/GVxcfEPaddf38mOfK6//nqVQ3mjoAvT09Nj7OiLNhFAAAEEEEAAAQQQQAABBBBAAAEEwiAgEz9qXayrG00A2fJ0z549VdOmPTncjhQ//fTTCxoHLbdIjrSjH9pEAAEEEEAAAQQQQAABBBBAAAEEEAijQGVl5WCZBGp8JVPjOSHLnn+zcOHdkqalV0ipK65qa2s3NwpyM1dhhfFgomsEEEAAAQQQQAABBBBAAAEEEEDALoHly5e3l0mg7EYTQbY8LSwsnD927Nh2VubxxhtvHN84WLkK6yQr26ctBBBAAAEEEEAAAQQQQAABBBBAAAGXCMgkUIyUqY0ng+x4LldMlT7yyCNDLEw7evfu3QsaxaoWqI+2sH2aQgABBBBAAAEEEEAAAQQQQAABBBBwk4BM/oxqNBlk29N58+ffLHlbMtH0r3893bdxoFVVVcPcZEosCCCAAAIIIIAAAggggAACCCCAAAIWC2zZsqWbTAjlNp4UsuN5QUHBR6njxiVYEf62bdteahTjSnluyeSYFbHRBgIIIIAAAggggAACCCCAAAIIIICADQLZ2dnxMgn0WqNJIVueyi2FxQ8++OBhZlOYMmVKp8YByuL0R5ltk/oIIIAAAggggAACCCCAAAIIIIAAAu4XiJZJobGNJ4bsev5ZZuY1wmHqqqk169ZNaRTfUrPtuX94iBABBBBAAAEEEEAAAQQQQAABBBBAYK9ARUXFYTIxVNJocsiWp/lbtsw+99xzW+uyp6WltZLAKhqCk6uwrFwsXjcs6iGAAAIIIIAAAggggAACCCCAAAIIOCGQtWlTG5kY+qxhcsiunzU1NUX3T506UDenrKxvL2wU20Jpx9RVXbpxUA8BBBBAAAEEEEAAAQQQQAABBBBAIAwCMjGkbimc1GiCyLanX375pdYthenp6TF79uzZ3BCYXD12eBio6BIBBBBAAAEEEEAAAQQQQAABBBBAIJwCVVVVR8sE0b5b9Romi6z+uWXL1tk631L47rvvpjSKZX44regbAQQQQAABBBBAAAEEEEAAAQQQQCBMAlu3bm0vk0QLG00U2fJUfUvho48+avQqqujdu3dnNQQkV2Fp35IYJl66RQABBBBAAAEEEEAAAQQQQAABBBCwQkAmiGKk3N8wUWTnz6++yvqjxBzyelbTp08/tFE8c63IlzYQQAABBBBAAAEEEEAAAQQQQAABBDwqUF1dfZJMFlU1mjCy5em2bUWfnX/99W1DZSoqKnqhIZAdOyoGhFqP/RBAAAEEEEAAAQQQQAABBBBAAAEEfCiQk5OTKJNFyxomjOz6KQu0l0976qmjQyGcODFdxdTw+CKUOuyDAAIIIIAAAggggAACCCCAAAIIIOBjgYyMjFiZLZraMGNk588lS5f+NSoqPaYlzlVr1tzTEIesi9W/pf15HwEEEEAAAQQQQAABBBBAAAEEEEAgAgTklsJTZdKotmHiyK6fJSUlS2+66abOwUjHjRuX0CgWrsIKhsV7CCCAAAIIIIAAAggggAACCCCAQCQJbNixo7NMHK2wa/KqUbu1r7zyxlliG3CB988///zGhv137NjBWliRdCCSKwIIIIAAAggggAACCCCAAAIIIBBMoP6WwkcbJo/s/Llq1aqXhg4d2qq5eFJSUuLlqrCK+v7nNrcP2xBAAAEEEEAAAQQQQAABBBBAAAEEIlhAJo9Ok8kj228plDWuCm+//fZBzVE/88xzVzRMoG3durXZfZqrxzYEEEAAAQQQQAABBBBAAAEEEEAAgQgR2LBhg7ql0PZvKVSTVO+/P2eKsB6wwHtqampccXFxw1VYC+T9gLccRsiQkCYCCCCAAAIIIIAAAggggAACCCCAQFOB+lsK/95wJZSdPzdv3rxizJgx3RvHMGXKXb9v6HPLli1HNn6P5wgggAACCCCAAAIIIIAAAggggAACCOwTkFsKR8hEUlXDZJJdP2tra+sefvixa6Tjn6/GkjWyVq5c1XAro1pgnquw9o0KTxBAAAEEEEAAAQQQQAABBBBAAAEEDhAoKirqKBNIS+yavGrc7oKFC5cde+zJvVQAF19y2ZSG9+QqrZMPCIoXCCCAAAIIIIAAAggggAACCCCAAAIINBaQiaQYKfc0TCjZ+XPnzp11f7j2hqlJ3Xtd/tHHnzR0VZCWlhbbOCaeI4AAAggggAACCCCAAAIIIIAAAgggcJBAVVXVsTKjVN4wq2Tnz1dfe71u5Kiz6qTPvd3I4vJjDwqIDQgggAACCCCAAAIIIIAAAggggAACCDQVkEXV28mM0lw7J68a2l6zdm3d/K+zGl7Wjh8/vn3TeHiNAAIIIIAAAggggAACCCCAAAIIIIDAQQIyoxQt5eaGmSWnfq5es2amBBPygu4qzoOCZwMCCCCAAAIIIIAAAggggAACCCCAQOQIVFRUDJJJomKnJrBUP9dcc31aY+H09PSYXbt2dZO31Dcmqkm1t6Woby6skKIe6meulJlSrpOSIiWhcRs8RwABBBBAAAEEEEAAAQQQQAABBBDwscCaNWtay4TQu1Icefzww/K6a6+/aeLKtWtHSodPSfl5kSzjvc+TKidJifHx8JAaAggggAACCCBgqQCXt1vKSWMIIIAAAggg4KSATAKpzzK/k/Kqk/1a1Nd2aecKKR9HR0fXWdQmzSCAAAIIIIAAAggggAACCCCAAAIIuFFg27ZtvWUya6MULz4WlpaWdnejKzEhgAACCCCAAAIIIIAAAggggAACCFgokJmZGSezV894cQarPuYzLOSgKQQQQAABBBBAwFcC3ELoq+EkGQQQQAABBBCQySA1EfSxFC+uMXWm3E74GaOIAAIIIIAAAgggcKCAFz/YHZgBrxBAAAEEEEAAgUYCagJo586dSbLp+0abvfL0U5mAS/ZKsMSJAAIIIIAAAgg4JcAEllPS9IMAAggggAACjgkkJiYWS2fHSLnfsU6t6+hbmcSKt645WkIAAQQQQAABBLwvwASW98eQDBBAAAEEEECgGQG5EmuPlL+Vl5cPk7d3N7OLWzd1k8AedmtwxIUAAggggAACCCCAAAIIIIAAAgggYIPAd999106uavpCipceyTZQ0CQCCCCAAAIIIOBJARZx9+SwETQCCCCAAAIIGBWQmSv1uedGKf82WjdM+y+Wfo+Xq8jqwtQ/3SKAAAIIIIAAAq4RYALLNUNBIAgggAACCCDghMCOHTsGdurUaaH01cWJ/kz2kSITWEtMtkF1BBBAAAEEEEDA8wKsgeX5ISQBBBBAAAEEEDAiIAu8r1uxYkVPqTPTSL0w7fts/ZVjYeqebhFAAAEEEEAAAXcIcAWWO8aBKBBAAAEEEEDAeYHompqaC2NjY992vmtDPfaXq7ByDdVgZwQQQAABBBBAwGcCXIHlswElHQQQQAABBBAIWaAuLi7unZycnB5SIyfkWs7vOMH5LukRAQQQQAABBBBAAAEEEEAAAQQQQMBVApWVlWku/nrC2sy6ujhXgREMAggggAACCCDgsABXYDkMTncIIIAAAggg4D6BVq1aqauw3PqISY2K6u/W4IgLAQQQQAABBBBwQoAJLCeU6QMBBBBAAAEE3C7Q2+UBnu7y+AgPAQQQQAABBBCwVYAJLFt5aRwBBBBAAAEEPCLQ1+VxXuTy+AgPAQQQQAABBBCwVYAJLFt5aRwBBBBAAAEEPCLQy+Vxnujy+AgPAQQQQAABBBCwVYAJLFt5aRwBBBBAAAEEPCJQ45E4CRMBBBBAAAEEEIhIASawInLYSRoBBBBAAAEEmgj82OS1214uc1tAxIMAAggggAACCDgpwASWk9r0hQACCCCAAAJuFZjn1sDq43rP5fERHgIIIIAAAgggYKsAE1i28tI4AggggAACCHhE4DuXx/mRy+MjPAQQQAABBBBAwFYBJrBs5aVxBBBAAAEEEPCIwHo3x5mbm7vazfERGwIIIIAAAgggYLcAE1h2C9M+AggggAACCLheIDo6Wi3i/qpLA32zf//+FS6NjbAQQAABBBBAAAFHBKId6YVOEEAAAQQQQAABlwvU1dUdJSG68VbCo2SC7XuX8xEeAggggAACCCBgqwATWLby0jgCCCCAAAIIeEVAJrDU56KfpPRyUczrJJZBMoFV56KYCAUBBBBAAAEEEHBcgFsIHSenQwQQQAABBBBwo0D9JNE4l8V2BZNXLhsRwkEAAQQQQACBsAgwgRUWdjpFAAEEEEAAATcK3HvvvZ9LXKtcEtsiiWOhS2IhDAQQQAABBBBAIKwC3EIYVn46RwABBBBAAAG3CcithIMkJjd8619Pufpqi9t8iAcBBBBAAAEEEAiHAFdghUOdPhFAAAEEEEDAtQIyabRGgvu/MAd4K5NXYR4BukcAAQQQQAABVwlwBZarhoNgEEAAAQQQQMANAnIVlvpHvqVSjgxDPJnS5yjWvgqDPF0igAACCCCAgGsFmMBy7dAQGAIIIIAAAgiEU0AmsdpL/zlSkhyMI0/6GiiTVxUO9klXCCCAAAIIIICA6wWYwHL9EBEgAggggAACCIRLQCaxOkvfm6S0cyCG7dJHf5m8KnGgL7pAAAEEEEAAAQQ8JcAaWJ4aLoJFAAEEEEAAAScFZDKpeOfOnYdKn+rKKDsfapJsAJNXdhLTNgIIIIAAAggggAACCCCAAAIIIOBjAbkSK17KS1LseKh2433MR2oIIIAAAggggAACCCCAAAIIIIAAAk4JyETTMCmFFs1iqXaGORU7/SCAAAIIIIAAAggggAACCCCAAAIIRIhARkZGrEw8jZQyX3MiS9UbqdqJEDLSRAABBBBAAAEEEEAAAQQQQAABBBAIl0BmZmacTEZ1lpIsZYiU46QcL2V4/U/1Wm1X73dW+4crVvpFAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAQQQQAABBBBAAAEEEEAAAfcLZGRkxObk5CRs2bKlXVFRUce6ujpV2m/atKmN/IyTEu3+LIgQAXcKqN8v9buVW1jYc8eOHQPl9+lIKcdWVFQcLtu7qd+z9PT0GHdGT1QIIIAAAgggYIUAH6atUKQNBBBAAIGIEVi+fHmrIUOG9JSEj5RykpRUKSdICeXkeavslyXlaylLpKyVsiU6OrpKfvJAAAERkImpePnRS8pxUk6XcrGUHlJCeeTJTplSsqX8KGXtsmXLthx99NHl8rxOCg8EEEAAAQQQ8KgAE1geHTjCRsArAnIionXCICf0nvj7pJufTeO3p77dWvmpnjeU6vrnNfJTFfVa/VSTJo1LhbyulNLwU53w7a4vZfJTlVIpu6QUS9kkw/S9/PT1Q8Y4urS0tGv79u3PlkRvlXKsDQlvkTanS5klZbW4qnGImIfLfo/C7u6Vv39WQakrp+655x41KXyGlNukqMlhqx87pMF/V1VVvdqqVatVYqz+TvJoJGDD76H6f1DD/3/U3zT1/xv1/xH1/5iS+qLGpUhKoRQ1wa/+FqqSL2WblNJIGSsb/IXP2Uek/e1yVpfeEEBACXjiBJGhQgAB7wrofiDzyocg3fy8O6IHRJ4i46SuIvLlQ8a2vSR2gZRHpIR69YdVFu9JQ/83NyrqfyOjo9Vko68fEf57dNDYeuXv30GBG9ggY64+g/aRMlbK3VLaSHHqoSZW7i8rK3tKJqbVZAkPEXDx7+H/JLwn5B8SPujQoYOa5PLlw8X+IXtHwt+ukDHYEQEEbBFgAssWVhpFAIEGAd0PZF75EKSbX4OPh3++ImOkTjx99ygvL+/Tpk2b+yWxcS5ITl29cHthYeFz3bp1U1e/+fIRwb9HzY6nV/7+NRt8Cxuzs7PjU1JS1JVWT0rp18LuTrytbje8UcxXO9GZm/vwyO/ht2L4JymLZMy0rvB26xh4xD8on5//dgVNnDcRQMAxASawHKOmIwQiU0D3A5lXPgTp5ufxo0HdVthFxkjdBuKbh0xc9ZaJq6ckofNcmtTD+fn56b169VK3dvrqEaG/RwHH0Ct//wIm0MwbMsaxslmtZfWSlNbN7BLuTV9JAFeI/aZwBxKu/j32e/idOF0k47UuXF5W9+sx/2bT9+PfrmYTZSMCCIRNIJQFZ8MWHB0jgAACCLhS4Ez5kOqbySs5aWgr5UmZvPpJtN06eaUOhNt79uxZJrHeqL6RzZVHBkEh0ERAjtdoKaNks1o37w0pbpy8UlH/SspGiXV6Tk5dgtrAw9UCR0t0a2W8HpDC30NXDxXBIYAAAtYJcAWWdZa0hAACzQjIB0utS/y98q94uvk1Q+WVTa/K2PzeK8EGi1PGTv0/cIyUd6R47R90NlRWVo5KSEjwxdUHEfh7FOzQjPLK37+gScibMq6d5MdMKWe2tK/L3ldXmZ4j4zDPZXHZGo6Hfw9/3LZt2wldu3ZVXzDi2YeH/feZ++Vv176EeIIAAq4T8NoHdtcBEhACCCAQQQK7c3Nzr/VDvnl5eW0lD7X2zX+lePH/hf1at26trj6Yor7BzQ9jQg7+EpBj83zJSH3DnNcmr9RAqAXlv5QcpkuJUxt4uFpgcFJSUq6MVZKroyQ4BBBAAAHTAlyBZZqQBhBAIJiAfKDkCqxgQN5670T511W1gK6nH3JIDpYEFklp5+lE9gf/lTxVV4t4dm0s3b8T+wn89czLVzHIWKrbuaZJGe+TUVFXOaq/fdt8kk/ANHzwe1gpyfWSsdoeMEkXv+EDf99cPeriw4TQEIh4Af7VNuIPAQAQQACBkASek5MCT09eycmBWovnRsl2hRS/TF6pwVNr92yS3LqrFzwQCJeAHIPqyiW1uLZfJq8U5UAphZLbyeoFD1cLqPXVltcfh64OlOAQQAABBPQEmMDSc6MWAgggEEkC6jYgT5+QygmNuuJ4qpQnfTpwXSQvNYmlri7jgYDjAsXFxYnS6XopQx3v3JkO58vv1yXOdEUvJgR6SN3PubXahCBVEUAAARcLMIHl4sEhNAQQQMANAtXV1aly9VWVG2LRiaH+REZNXE3Wqe+hOvES6wo5yT7SQzETqg8E5JjrmpiYqL7FU00e+PnxpuQ6SQpLcLh7lEfcc88917s7RKJDAAEEENAR4H/AOmrUQQCBkAXkgz5rYIWs5cod/yWTVxNdGVkIQanJKzmReVl2vSKE3U3tsmfPnqiNGzdF5W/ZElVaWhpVVlYWFRcXF9W+XfuoDh3aR/Xr1y8qKekQU30YqDxExu1HA/uHdVfdvxNhDdrGzmXsPPP5TMZOfdNgrhR1BZatjx07dkTl5G6IKtm5U37HyqKqqqui2rVrF9W+ffuobt26RiXL75j6nXPg8Xfp424ZJq3/vzkQn1YXPvw97CFjVKCFEYZKfvD30t+uMAwxXSKAgAUCnvmAZEGuNIEAAghEnMDy5cvbDxkyZKskrtamMfrYunjx4j7Dhg2rNlrRLfvLCcH9EsvddsQjt0xFzftq/tfzv86a/fU33y7NXbt+Y2V0dWlCVNTusrK42roOe6Lb79kTU1ERnVAXX5MYGx2f1KFj+0FDjxh82imnnPTrU04+OfGE4cfbdcK9R3LuLScTW+zI3ctt6p4kcmJ28KiLpfq7slZKr4PfNbdF2t6zePGSV+Z9Pf/Tr7/5ZtWK774r3Lxr1662NTUVBR061EStXVsrPagJpOiooUNjuuTnJ8ijU98BAwYOHzY8VX6/bjjt1F/1UBNbNj3ukmPiQZvaDkuzFv5uHHR+If+YED137tyYrl27xuzcuTM+OrpT6zZtqjt27HhIt0O6JQ3o3avn8F+dctLFKccdd2hsrPoeAEsen8oYnS0teWKi0UJ/S/BoBAEEEEAAAQQQQAABxwTU1UfygXih+lCs86ioqBjkWLA2dCQ5X6yTd7A6cpVV3QdzPp77+6uvPi4qKtXUpR59+vRpc/jQoUf/5a/33L9ixY9VwfrVfK9I6rW1gdbTTWpaeuIE2MmBEUf19yVb1zNQvdzc3DnpDzxwTFpamulZjC5dDuuYlnbZmLfefne13AodqEsz2y910tzuvnQhrIwrOTk54fY77x6dl5+frxtPk3qeWZOtSdwhv7TSn7YQQAABBBBAAAEEEAiLgHz6fSjkT8AH73hPWIK2qFNJZ/DBKZnbMnfevJmjRo3qblGITZuJ/eMf/zTqp59+UpNOVj7UBCbrXTbS1sVt1ARPRUAcH9O1bK7e1q2FH/zlL3/pbRNu9EknndTvgw/mvNtc3ya3HWVTzI43q+tgU6AxDz009Zba2lrdsBrqzbcpPsubbQjY6E/LA6FBBBBAAAEEEEAAAQScFJCrDUYZ/RDcaP/1GRkZpq9+cDLfxn2tWbOmteRS0igfU09XrFix/tJLrxrYuA8bn8fee+/9N8gtNqZiblJ5io3xeq7pJjYhv/RcojYGLGhnhwzXwo7l5eXF//znP0+QcA+67cyOFM75zW+SFy3KtvLKsQpJsYMdsTrdZgtDFfBtO+O8/PKrUnaVlgbsO8Q3PPHlAiHmctBudvrTNgIIIIAAAggggAACtgqUlJQcctAnXAMbioqKDrU1QJsbl1RfMJBuwF1ramrq1O19Eq7jk3nJQ4f2mDPnI/WNglY9BtvM7pnmdUE9k6DNgYpfBymmL4tR4/D1Nws+SE5OlWXjHH9Ej7/5lkt3796twrDisUAacWQCzk4pXQg7Y1Jtp6VdPtzklVh/tTtGK9p3q78VudEGAggggAACCCCAAAIHCagrp+RDsJmJj5sPatRDGyT3EbonAY3rySRg3SWXXK4W/w3nI+6J6f+e0TguE88LMjMzTa3ZFU4IK/vWNbQyBq+2JXbRUj7RNWxc7803Z6orA8M66ZM6enQPuXVxY+O4TDy/yqvj2hC3bu4N9e38+dDUR+7SjU/qlas1Ie2Mz4q2dfOzom/aQAABBBBAAAEEEEDAcQH5AJyu+yFY6i31wof8QKjZ2dnxkkO5ifz3Vt2wYWPdiNTUIwL14/D26ImTJk2uqrJkjfc7HI7dld3pHh+uTMbhoMTOzK3Je+nVFyH83/89eIXDoQfsbujQoa2WL18xT/e4aFLP07cSNskl5JcBca19I27d+vXFIQd18I6uv7L44JBD22ItM60hgAACCCCAAAIIIOCAQGVl5ZGhfdxtfq+8vLwkB8K0rQvJ6obmMwt96+Il/6sb9Mtfuu52uzEXXnSjBevAKIgutg2ARxoO/Wg4cE+PpGdbmKIRJ8XU2nJqInbcNdeNtS1I/YZjP8+cO/vAEdd69Zp+COGvqZWxVHIq8j9Nun20boxS7xKn4tTtRzc33f6ohwACCCCAAAIIIIBAWARycnIS5MOvmZPLS8MSuEWdbtq0qY3kb2pdHpnAqzt86NGnWBSS5c2Mvera29TVKyYfr1gemMca1PXzWJqWhytuN+naNdS74467brM8MOsajF25cuXchlhN/OxrXUjOtqSbs1NRqqvliou1L8J626k4dftxu79uXtRDAAEEEEAAAQQQQKCxgFqXZqbuh1+p94WUsK5F0zgZnecS/z1StB9y9VrdmWef5/Zb7KIf++e0J7ST3F+xt46xX+rsZzD2zC/56+QhUmqC3NTjtddfnyl9u/rvTGpqaoJ8A2i+qUTr6j7QMXZDHd28nYz9/TkfvqIZp/oHDlcff5p5OXYFnJPjTF8IIIAAAggggAACPhWQD73n6X7wlXq1ctWBp9dtWbNmTWsT+e+tesutk+bL4eHqk5v6wzfm088+X2Iy34i+CkvXzqd/PkJKS8yu03VT9bIXL8lPSUmJD6mzMO80duzYbnKlo6mrOSXlnmFOQ6t73THW6kyz0pS7/na2bpzLly9vpdmtI9V083IkODpBAAEEEEAAAQQQQMCsgHzg7az7oVfVq66uPsNsDOGuL2lcYMbgxZdm1HXq1LdzuPMItf/DDjusY05OrtlV3TuF2p/f9tM9VvzmEGo+4qXWvqrQdSss3FZ3zDEnDAq1Pzfs949/PGp2sfpH3ZCH0Rh0x9hoP2b2v+CCCw7VjVPquXoNQN28zHhSFwEEEEAAAQQQQKCRAB/IGmFY/FR9Y6D4mrkaJ0NC8sJVRwHlJH91++Rm3eNs+/btdf0G/MI134gWMNEmb1x55R/O1c25vt7kJk1GzEtdt4gBapKoeJ2ha6bqybpXf2/SpBdeRufm5n5mJm91ZagXEm0co26+jduw+/lZZ53VTjdOqefqbyLUzctuc9pHAAEEEEAAAQQiRoAPZPYNtdhO1PWVeqWZmZkJ9kXnTMuSx0ATBnX33nd/vkQa60y0lvYSvWTJ0nkmcldX1MRYGpFHGtM180h6locpXtqT5Bs2bKySKwY9N5GjEG+66c+mrm4Vt1TLB8PmBr3wu6EWcteNU+oNsJnQVPO6eZnqlMoIIIAAAggggAAC+wX4QLbfwspnFRUVA3RtVb2ysrIUK+MJV1uSyt26DgUFW+v6JCefEK7YzfZ72WWXmbmVRrENNhuDF+vrHi9ezNVszGLVRddL1fvjn269xGwM4ay/evXaCSby/yycsev0rZurTl+6ddRaarpxSr0jdPt1op5uXk7ERh8IIIAAAggggEBECPCBzPphzs7OVh/gzXxT1jTro3K+RTFQtw+W6B5jf77r7pUStadvofxmwcK3dfOXer44DoweebpeRvvxw/5iNU7Xa83atRViEOdlh7Rbb22jm399PU9d5aqbq5NjLBNYbXXjlHrJTsZqtC/dvIz2w/4IIIAAAggggAACAQT4QBYAxsRmMZ2u6yr18uXWQU+fVDbQSS7ddR3y8vPrevbt6/mr0C666CIzV+JVqXXUGjwj5afuMRMpPo3zFKsVul43jh9/XeO2vPp8+fIfZ+oaSL2jvZS3bp5O5ihrYHXTjVPqdXMyVqN96eZltB/2RwABBBBAAAEEEAggwAeyADCam8VzhK6pqrdjx46Bml27rpqkc6muxRPT/63WvvL01Vf1AxIt6wwV6TpIvZ6uG1ibA9K1sjks1zUvTtpXupSUlNQlJf2ig+uS0gjoL+npp+oeM1LvHo0uw1ZFN08nAx4//k8n6cZZWFfn6mNSNy8n/ekLAQQQCLdAxP3La7jB6R8BBBDQFcjLy2srdTN160u9PycmJq4zUd9tVS/SDeid/753g9St063vonp178x6734T8Zxsoi5V/S0wVDe9Dz/86NNt21bt0q3vpnofzZ79nUws6IZ0vW5F6jUvcMKIE37d/Dstb02KiipreS/2QAABBBBAAAEEEIhYAf5F0ZqhF0e13tMcXU+pp24F8s0/WkguyqNKiuHHpk0/1SUnJ3tqbZpgR9HIs88eahhhf4UZwdr243v7Uzf2zI8WwXISncnGhPbvPXbs1acFa9tj70VnZy/en5zxZ628kq/x1H6u4WB+MetzctQ3qOo81joYp1ZXOkmpOlqdUQkBBBDwqIBvTmY86k/YCCCAQKgCabLjuaHu3HS/nJyckdHR0Xuabvfq68LCwnYSe7xO/FnffPtpbm6uWmDaF49V33+fU1qmfWGBXMVW54dbKX0xli5L4nKdeNT59JIlCxfr1HVpnbqsbxZ8aSK2ribqUrWRwNnnn39M/+Tk1o02GXn6ppGd2RcBBBBAwJ0CTGC5c1yICgEEENgnIOvJyJ0PUWY+fF85YMCAgn0N+uBJ165de+umseCbb6br1nVjPbm1tDx70eIdmrG1kSvSfHM1mqYB1ZoIZGRkxMqmY5tsDunlypWr8pYvX14a0s4e2WnBt9/ONRHqoSbqUnW/QPTkP93y2v6Xhp+9Y7gGFRBAAAEEXCfABJbrhoSAEEAAgf0CcjVDTIcOHebu32L42VdS4xXDtdxfoY9uiN9/v/R/unXdWu+777+fpxtbly5dOuvWpZ4/BY466ii13p7W4/vlP/juttTvln3/gxbGz5UGm6hL1XqB8y747UVnnDHqF7og+fn5P+rWdXs9dRuhkw+3exAfAgj4W4AJLH+PL9khgID3BW6XFHQXU96zatWqX8utg35cI6Ov7tCuWFG8TbeuW+vJLaKLdGNr27ZtxH0Toa5VpNTr27dvom6ua9ev/1y3rlvr5W8pyCsv360b3hDditT7WeDww4/u/cxT02ea8Pi6V69e5SbqUxUBBBBAwCUCTGC5ZCAIAwEEEGgqsHPnzkGy7aGm20N9XVlZefYRRxzhi28CayZnrX+JLyraHpWXt7iymfY8vSl3fc5yEwl0N1GXqj4UaNOmTTfdtDas+2m9bl231ouOrSuWdfN0wztctyL1oqIGDhzY7aWXn1nTs0cPMxx/NlOZuggggAAC7hFgAss9Y0EkCCCAwD6B7Ozs+I4dO2bt22D8yesJCQm+uxKiEcORjZ6H/HTbtr0XX/lmMfuGxAuKthc3PNf42UujDlX8LaDW3dN6FBdvMXMsavVpd6XY2riqwp//duh0pTXZrtOR3+ocf/xJp3708cf5J54wvI2J3PYsXrz4WxP1qYoAAggg4CIBJrBcNBiEggACCDQIpKSkPCHPdU8id82dO/cPUt+Ptw42EHVqeGLkZ9nP39bnO5fy3bu0728SPy1LI+7s6zmBdroR/xQVZeZY1O3W1no1NbVVZWXad6DxLYQGRyc5eWiPxx6fNvOLzE++PGzgQLPnKrcNGzas2mAI7I4AAggg4FKBOJfGRVgIIIBAxApUV1efJMnfoAsgkzS/GjlyZIVufY/U0/r/V+nPE1geSTH0MHftqjAz3u1D74k9I0Sgg26eiaWlvrvCMS6uRiawynRJtC11O/RAvWiJcW9JTk6O315T07Z7m8Tuv0o95YTTU0+dfP55vx4qVyBblcbTVjVEOwgggAAC4RfQOgEIf9hEgAACCPhT4LvvvmsXFxf3hYnsHm7fvv13Jup7pWornUBjYvb+Y746cfLVVVjxOhj76/huwmF/ajzTFNA+pDZu3Oir360Gv/q/HQ0vjfzUtjTSSTj3zc5eXFe+2/iFd61bt4o65JBDopKkdOpky4WgN8qXmBgPLJyY9I0AAgggEFSACaygPLyJAAIIOCcgX4OtJlbeltJas9dNcuvgXZp1vVZN6/9f7drtvTPKdxNY8k2C2rd8ycD7blF7rx3MLoy3RDemIUOGxC5fbuY7BXR7tq9eTVxcq7ZttZdh8usXaewDT0k5bt9zFz3ZKv8/fM5F8RAKAggggIAFAmbvK7cgBJpAAAEEEKgXuFh+nq2rkZ+ff4rcOlijW99j9fauxm405nZt26oqagLLV482HdqbuU1pu68wSMYKAe0JLLkFWncC3oq4bWkjtjqmdf3kt077Wn+rdDqizgEC6lb6SPn/4QGJ8wIBBBDwswATWH4eXXJDAAHPCOTl5akF2zNMBHxzr169Npqo77Wqm3UC7to1Kap79+4JOnXdXKdbl6RDTMS31URdqvpTQPuqoR49eiT6jaQmpjqha5Lud2pE5frNwwP53Ca3Dq72QJyEiAACCCBgUIAJLINg7I4AAghYLSC3Dsb07NnTzLpXi++9994nrY7L5e1pnZx07tw5qlVioi2LrYTTK3nAoUeZ6D/PRF2q+lCgsrJyi25a/foNHKBb17X19sQm9uvXTzc8f91PqavgXL03pavHneuOnhBAAAEEnBRgAstJbfpCAAEEmheYJJuPbP6tlrfKoslnpqenR9pC3FoTWEpzUO/kPi2remuP5OT+qboR7969u0C3LvX8KVBeXq5921tyct9f+U2lR/euvU2sgbXWbx4uzud9ie1yufrKl18k4GJ3QkMAAQQcE2ACyzFqOkIAAQQOFtixY8dA2frwwe+EtqW2tnaMXBlQHNrevtpL+6TwiKFH+O0EO3rI4COO1x1dOQaLdOtSz58C8m2oZbqZDf3lkGt067q13tAhg81c4fiDW/PyWVxPSz4XyORVpP1jTpTk7OjDZ8cN6SCAgMcEmMDy2IARLgII+EcgOzs7Xr46fL6JjN6Ji4tT/+IciY+fdJM+Yfjw3+nWdWO9pKSk9sOPH6b9/3O5fZWvmXfjwIYxpvrFr7VuLR0yePChsh7f3m9LCGMKlnYtfzNGmmhwnYm6VA1N4Hcyg3OTlIibvAqNh70QQAAB/whof+D1DwGZIIAAAuERSElJ+af03EOz97Lc3NwrpG5E3iqxePFi7W/OO/GE4SlRQ4e20nR3XbXBg48anJiovW72h3LSF5HHkOsG0n0BPa8TUmxsbNSJp5xyrE5dl9aJGT78+BG6sRUUFBTq1qVeiwKZskdX+Rv2hvzk71iLXOyAAAIIeF+ACSzvjyEZIICABwWqqqqGS9jjdUOXNWpO7d+/f4Vufa/XGzZsWLXkoHVlw+GDDotKSej4C68bNMQ/6oyR5zU81/j5ukYdqkSGwCe6aZ51+plqXT9fPPr2PbKTTHrr5lIm38pYrluZegEFFsk7R8nE1Sgp2uu1BWydNxBAAAEEXCvABJZrh4bAEEDArwKbNm1qEx8fP9dEfo+0a9duiYn6fqn6nG4i548Z7Zd1eqLHjDnfzGTBV7qG1PO3QElJifa3551//ujf+uU2wtFjTju6VSvtCzZfkaOEK4Os+VWplGb+JqWPTFoNl/I9ttbA0goCCCCAAAIIIIBAvUCd5sPHgNFCMkuTRVXLzczMjPOxT8ipicWRuo5Lv1smJ5Xev43wuONGHKZrIPVqpUTcP2TpeoV8YPpkR3FSf6sKdb1Gn/+bS/xA8fEnn83UNZB6ZtbOcpzPRJ52VFV/n9T/Ky+X0luK7/9WSY5aD8cPFDpEAAEEEEAAAQT8KqD1aUwq+dWjpqZmjK6JqldUVHSoX22M5rVmzZrWZiwvuuiSM4z26bb9X3xpxmsmDJ5wWz5OxKPr5URsbutDrCbren308Sfq21E9PekwYsSINnv27NElUPU6uG1Mg8VjJtEQ61bJfsVS8qUskzJHynNS7paiJqpOkdJXSjsp0cFi9eN7krPWw48W5IQAAggggAACCIRFQOvTmFQKS7A2d7phw4bOuh719a6zOUTPNS8un+iaZn2zYLMk7NkT7KOOOqqbrKWmm76qd5znBtyCgHXBLOjac02I1UBdL1Vv1KizPXUFUtMBmjVr9nUm8l/ftD23v9bN1e15eSU+/L0yUsSJAAIIIIAAAr4V4APZz0Obnp4eIxbZuh5Sb76UiPsX6ZZ+McQk1YRp3QUXX3xSS3249f3nX3zJzNVXii3erbnZGZfu8WJnTG5tu/7vVomu2QdzPlLfwBfr1vyCxZWScn5bufpK3cam+7g0WPtufE83UTfm4sWY8PfiqBEzAggggAACCPhKgA9kPw+nOPxR10Lq1cqtgx19dWBYlIzYtDLhWvd11jcFEornTrCPOebE5MrKSjOp32fREHiuGV00zyVqUcDidYOumap3aupZV1gUiqPNvPPOf580k7cX/2br5uvowPi4M/x9PLikhgACCCCAAALeEOADWVSUWrdK16G+nqdvw7H7SBWjJ8z4XpR26c12x2hx+zEvvDRjpZmcpW43i2PyTHO6bp5J0OJAxStR10zVk7Ww6rp27dre4rBsbe7UU88YXFtr5uKrujm2BmhT47rjbFM4Edcs/hE35CSMAAIIIIAAAm4TiPQPZJJ/rJRcXQep95zbxtRt8YiR+oYq7UdeXl7d4Ycf3dtteQWK5/dXXX21drI/V8wK1HYkbNe1iwSbQDmK2du6bqrebXdMURM6nrgFOiUlJX7lylVFZvKVuoMDWbp5u27Obs7JS7Hh76XRIlYEEEAAAQQQ8KVApH8gk/wf0jWQegVSInKdIqO/DOKk1gjTfsydOy9X+owz2q/T+x977En9du7cqZ1nfcUjnY7bTf3p4rkpB6djETP1zXDaj4qKirrTzzwnzem4NfqLfnPmWzO0E/254lr54YnJuqY+unk3bYfXegL467lRCwEEEEAAAQQQsEwgkj+QyRpFR+rmr+rJSd8AywbC5w0J1+FmrFXdhx957G1hcu2JZ3JycsJ3y5apSU0zjyyp7NocnThMdfGciM3NfYjbXF07VW/Dho11yUcckezmHCdPvtPMtw428Bzr5hyDxdaQgNGfwdrkvdAFjLo37B96D+yJAAIIIIAAAgggEFSg4QOW0Z9BG/XAm2vWrGktOWt/e5fU/aMH0nRTiNFiZuoEW75xrO6Sy373d0nKjRM8sS/PeHWh0d+jZvbv76ZBC0cszZiEtCkcsbqpT0HqFxJUkJ0+/uTT4qSkpA5uyqshljPOOCdVXSlm8rFE6rvx70dDmkF/6uYetFHeDFkA/5Cp2BEBBBBAAAEEELBHIEI/kKnJlFd0c5d686R49iTIniOp5VbLy8tNrYWlxmtXaalMYl3+gPTmJv/YJ59+dpaKz+Tj3ZYV/b+HrqH/ZVrOUOye0/VrqDfzrXfXq6sJW+7NuT3OHD1m2LZtZpe92pvhIOeitr6nhjEy+tP6SCKzRaPuDftHphZZI4AAAggggAACNgg0fMAy+tOGUBxrsrq6+nSj+Tbav1yet3MsWJ91JHYPN7LUeirjV3fLrbc/KTQxYedJTY2bPfv9L7QSObCS+ko1V1754rTxgSyhv3I6Tjf2J1rtQxcLvOdX879e2adPnzZuyPGKq64ZJZPfgYMN/Z2n3JCPmRhCT/XAPc30Sd39Ageqhv5qfws8QwABBBBAAAEEEDAlEPpHsAP3NNVpGCtv3bpVneBVHZiNoVcRvcC22aET6VZSSg2JB9j5kUcez+revXvYJhOTk49J/HbhwpUBwjO6+QKztn6pbxSuYX+/5G82D/FIazAx8/PHH1fmHnnkKZ3NxmOifvSUO++8obZWze2afqh7D111VZmOi66CTl/UOVgA/4NN2IIAAggggAACCDgqEEkfyCRXdeugmatlbnZ0cHzamYzBsbrHXdN6b739bsXAgb84WaicvKUweuTIM09eu26dmYnQxqmotcGcjN/VR1ZjGCPPXZ2Ug8GpY0mKmb9z+9jzt2ypGjNmjOOLnqtbGP/zn+dn7wvE/JNTHRwC27rSZbAtoAhrGP8IG3DSRQABBBBAAAH3CUTSBzLJ9TLdfKXeHClMMlh0CIvlVBNjcUBVOcmuu/Lqa+Z06N37EIvCC9hM0i9+0eFf06bPVLcxWvRQV4Zw62AjcV3XRk1E/FMx7CjFkglW9eUJL778yt9T/r+9e43RoyoDAPx1u5Qu3RY1W6VbiFAqhl0kjUVK6AJtIiTaRGOgJP5Q4QdiUqNpIEQxmrZojNEfUCQGJWqMiZHEXxhioqSl0tIW2mpohVBk6XWB3qRdtttSi++Brt2W7V5mv9vM90zysvNd5pz3PGdmOnOYmW/u3POqADvh1ltv/+yr3d1luWfw1Lq0sgp5V6UK20ZVmM9ZCf9z0viAAAECBAgQIFAdgUY5IOvp6Zmeta2xXO+uXbvq4nkw1VkrKl9LmDZFlONX+/7fres3bHz3i1+67Rft7e1t5W7B9OnTW+/7zv3fT4NlZZ6uLHeueS8vq2/e213u/MPx01kth1ruwIEDh5f/6Cc3R56VePbchFtuWXTV3556Kv1KYDmnl6OwieW2rVV5WWFqlW/R6uVftB7VHgIECBAgQCB3Ao1wQBZtTIMlW7O2df/+/dfmrmNzkHD0x5SIsjwPa3Dfbtq85d2l9963btasK7rG+WtqE6+77sYrH1r589+//sYbg6so1/xXctBNVU8xK27VE81BhWG5JKvnuZY7ePDgoUcffexrXV2L0vOxxnVVato+lyz51uefWbuuXM+SG5x236FDhz6Ug24adYqDGzeW+VFX4IvDCozFfPB3hy3UhwQIECiYwLgODApmoTkECFRAIB1kZSl2QkxZlqvFMtHEe6Len2WpO/GsWLFi3pYtW3qampr6+vsveKe5+fCJI0eOnIyrck7u27cv/X23s7PzPcdly5ad7Xn26yxpFHaZ+HWxmS0tLTujgWW/qqO/v7+0fv3G0tNr1jz37IYNv97+0itr910UNsoAACB9SURBVB/v3XP4wgt7S9u2nYg6B/omrctNbW1tLe3ts9o6Oj4xZ/4NXYsX3nTjlzs7Oypl/3AU/O3YjAZyqFQ9uSu3EfZJ1eqUsEzr9qMRd1Wizu2vvHLw6dVrHlm99u9P/vP5F7u3bj1xsFTaNHjbGlxtU1wdObmjo+Oia+Zd/5kFN3YtnT//+nmtUyr2OwyXxva1Y3ACeZ+3bdS2B/nX1l/tBAjkQyA3J4j54JQlAQJnCxT9gOzo0aOXTp48ufvsdhfxdZ4GFQf7xzrYGa+3Dn6vUvNxC2CpZ29P6Uhvb6m39+1Sc/PEUmtra2natKmluBqkNDXmqzD9Kuq42+DV0NJF3ycN3erKvRueaXD4jxG3Va6W90vu6ztaeu2110r/eeutN3t7j+w6fuzEydbWKc1Tp7ZekX4xdObM9lKs95VOI5V/bdTzXDUqqmYdto1qan+wrqz+Hyyp9u/k9Xih9nIyIEBgJIGq/Cs/UhI+J0CguAJZD8jycPCzatWq5gULFuyI3msvbg+eblke+uR0tmfOxXo4J97Zcua7hXz1u+XLl98ZV+qdLGTrytCoIu+TysCTqYgwTc+BeiLic5kKyNdC18S+cFO+Uh5dtraN0TlV6ltZ/SuVz3jKzfPxwnjabVkCBCovYACr8sZqINDQAlkPyPJw8BNt+2l07r2N0sF56JPh+iL6Kz3QPF2JVfbbCYert4qfpSuvvhH9ZPBqGPQi75OGaXbFPwrXtF39JuKrFa+sdhVcHdvXC7WrvrI12zYq6ztS6Vn9Ryq3Fp/n/XihFmbqJEBgdAJFPYgfXet9iwABAhkFjh07dnUs2jCDVxmZ6mqxOKB+MRKaGRHP0Snc9M1oUbpt0OBV4bo2Hw06te7dEdn+OB8ZjynL/fHtGdHGwg5ejUnDlwkQIECAQI0EDGDVCF61BAjkV2D79u3nT5o06Zn8tqBxM48T0Nej9RdHPFsghZuiXY9EeGB7gTo1j01J62DE/ZF7kW4lXBvtuSTalfYdJgIECBAgQKCGAgawaoivagIE8ikwe/bsX0bmU/OZvazjRPRoPCeqKyTuybnGtsj/Y9GeNTlvh/QLJhDr5F+iSenZgHtz3rSvR/43RHv6c94O6RMgQIAAgUIIeAZWIbpRIwjUr0DWZzrECUNd7p+iPTeEdkMOGNRrn4xn7Y/+vCyW/2vE5eMppwbLLo06V0afuGVwjPhF2yeNsflV/XpYp4e7fzfigapWPP7K0u3GN8f2tWf8ReWnBNtGbfsqq39tsx669iIeLwzdUu8SIFBtAVdgVVtcfQQI5FZg7969F0TyabDDVBCBOMjujqZcEXFnRB4Gg9Ktj+l2pgcj8pBvpGtqVIFYR/8b8cP+/v6Ph0EefgX0WOS5KKIz8m6owatGXUe1mwABAgTyJWAAK1/9JVsCBGonMGHGjBl/iOrPr10Kaq6EQBoIivhtlD0l4geVqKMMZe6IMuZGzI9cd5ehPEUQqJpAS0vLzqgsrb/zIurxtsJ3Iq+7IqbF9vVkhOfJBYaJAAECBAjUm4ABrHrrEfkQIFCXAnFp/y2R2BfqMjlJlUUgTlr7Ix7YvXt3utIuncymqzFqPW2PBG6KZ3bNitw2O7GudXeoP6tAWncjNsa6fEmUcW1EPVyR9WbkcXvElMjtsYjjMW8iQIAAAQIE6lSgLp8xU6dW0iJAIINA1mc6xIlE3eyfog0t0fTeiIYe9K+nPsmwKo55kccff3zi4sWLr4oFvxexeMwFjG+Bh2LxByN2hLurQcZnecbSRdgnndGgHL/o6+u7OK7OuiOakH65MO1nqzGlW28fjlgZ0W37Ok1u2zhtUYu5rP61yHWkOhvteGEkD58TIFA+gbo5QSxfk5REgEA9CWQ9IKung59oQ9pXfiRiZsRFEdMj2iI+HDHtVLTG33QLWjoJmzwommN+UkS69TDNp0gDYenhxgPzAwNj6b00Dbx+/1Wd/Lee+qTaJLEOnBd1fjIiXa1xd8RHI8o57Y/C0gn1n1avXv3ywoULT5SzcGWdFijCPul0a4oxF32S9nmXRaQrXZdEdEaUc3ohCku/HvvniJ2xL/P8uCF0bRtDoFTxraz+VUxx1FU18vHCqJF8kQCBTAIGsDKxWYgAAQIEGlkgTjTSIGV7xOyI9GyfdEvUpyIujxhu+nd8uC1ic8Q/Iv7V09Ozp729/WjMu9IqEEwEYvtKg/5pkDgNas2JSNtXGkDuiEj/o2Co6e1489WIdRHPR7wUkba3fXEybUA4IEwECBAgQIAAAQIECBAgQGBAYEKcfDdFTIw4L6L51HxdXlU3kLS/BPIiENvTwDb23naWbvVN7+Ulf3kSIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECORT4HySHDDaVQmzLAAAAAElFTkSuQmCC";

/**
 * component: Image
 *
 * Summary:
 * Used to render image files with proper accessibility and fallbacks
 *
 * Description:
 * Will render a standard HTML <img/> tag, but with a default fallback
 * to prevent missing images from rendering to the application UI.
 *
 * @param id: string
 * Used to apply a custom ID property to the HTML img element
 *
 * @param className: string
 * Used to apply a custom class to the HTML img element
 *
 * @param alt: string
 * Used to provide a description of the rendered image
 *
 * @return HTML <img> element
 */
const Image = ({ src, className, id, alt }) => {
    const handleFallback = (e) => {
        if (e.target.src !== img) {
            e.target.src = img;
        }
    };
    className = `izo-image ${className}`;
    return (React__default.createElement("img", { src: src, className: className, id: id, onError: e => handleFallback(e), alt: alt }));
};

/**
 * component: Input
 *
 * Summary:
 * Used to render a labeled text input field for searching or form entry
 *
 * Description:
 * Will render a standard HTML <input/> tag, but with default accessibility
 * standards handled, labels provided, and validation/error UI included.
 *
 * @param id: string
 * Used to apply a custom ID property to the HTML input element
 *
 * @param className: string
 * Used to apply custom classes to the HTML input element
 *
 * @param label: string
 * Description of what the user is asked to input, will hover above inputted text
 *
 * @param value: string
 * User's entered data
 *
 * @param onChange: function
 * Callback function to handle new data entered into input field
 *
 * @param error: boolean
 * Should be 'true' if the user has entered an invalid response,
 * and will render a red border around the input field
 *
 * @param validated: boolean
 * Should be 'true' if the user has entered a complete and validated response,
 * and will render a green border around the input field
 *
 * @return HTML <div/> element, containing an <input/> and <label/>
 */
const Input = ({ id, className, label, value = '', onChange, error = false, validated = false, }) => {
    className = `izo-input ${className}`;
    if (!value.length)
        className += ' empty';
    if (error)
        className += ' error';
    if (validated)
        className += ' validated';
    return (React__default.createElement("div", { className: className, id: id },
        React__default.createElement("input", { value: value, onChange: onChange }),
        React__default.createElement("label", { htmlFor: id }, label)));
};

const Modal = ({ id, className, children, close }) => {
    className = `izo-modal ${className}`;
    const handleClose = ({ target }) => {
        // ensure that inner modal clicks don't bubble out to the background click event handler
        if (target.id === 'izo-modal-background') {
            close();
        }
    };
    return (React__default.createElement("div", { id: 'izo-modal-background', onClick: handleClose },
        React__default.createElement("aside", { id: id, className: className }, children)));
};

/**
 * component: Spacer
 *
 * Summary:
 * Used to create empty space between elements in a container
 *
 * Description:
 * Allows other UI components to be more reusable an extensible
 * by replacing the need to add margin to the top, bottom, left, or right
 * of an existing element, requiring style override of the UI kit default
 *
 * @param height: int
 * Used to define the height of the empty, invisible div
 * Note: should be used in increments of 8px whenever possible
 *
 * @param width: int
 * Used to define the width of the empty, invisible div
 * Note: should be used in increments of 8px whenever possible
 *
 * @return empty, invisible HTML <div/> element to create empty space
 */
const Spacer = ({ height = 8, width = 8 }) => {
    return React__default.createElement("div", { className: 'izo-spacer', style: { height, width } });
};

/**
 * component: Text
 *
 * Summary:
 * Used for any text rendered in the application to maintain consistency of styling
 * and reduce the need to write and rewrite custom CSS
 *
 * @param type: solid, outline, text
 * Defines the appearance of the text
 * `primary-header` is the largest, most prominent text such as page titles
 * `secondary-header` is used for any prominent text and headers below a page title
 * `section-header` is used for any text labeling a card or small content section
 * `small-header` is used for sections, or item headers
 * `sub-header` is used as a description under a larger header
 * `paragraph` is used for all basic text and body elements
 * `small` is used for understated basic text
 *
 * @param color: black, white, green
 * Changes the color of the text itself.
 *
 * @param shadow: boolean
 * Will apply the proper text shadow if true.
 *
 * @param id: string
 * Used to apply a custom ID property to the HTML button element
 *
 * @param className: string
 * Used to apply custom classes to the HTML button element
 *
 * @param align: left, center, right
 * Used to define the element's text alignment
 *
 * @param wrap: boolean
 * Used to define whether the text should wrap onto multiple lines,
 * or cut off with an ellipsis
 *
 * @return HTML <div/> element containing provided text
 */
const Text = ({ children, id, className = '', type = 'paragraph', color = 'black', align = 'left', shadow = false, wrap = true, }) => {
    // add default classes to optional custom className
    className = `izo-text ${type} ${align} ${color}${wrap ? '' : ' no-wrap'}${shadow ? ' shadow' : ''} ${className}`;
    // pass className and id to all possible return elements
    const elementProps = { className, id };
    switch (type) {
        case 'primary-header':
            return React__default.createElement("h1", Object.assign({}, elementProps), children);
        case 'secondary-header':
            return React__default.createElement("h2", Object.assign({}, elementProps), children);
        case 'section-header':
            return React__default.createElement("h3", Object.assign({}, elementProps), children);
        case 'small-header':
            return React__default.createElement("h4", Object.assign({}, elementProps), children);
        case 'sub-header':
            return React__default.createElement("h5", Object.assign({}, elementProps), children);
        case 'paragraph':
            return React__default.createElement("p", Object.assign({}, elementProps), children);
        case 'small':
            return React__default.createElement("div", Object.assign({}, elementProps), children);
        default:
            return null;
    }
};

/**
 * component: FlexContainer
 *
 * Summary:
 * Renders a <div> element with flex alignment set by default.
 *
 * @param direction: 'horizontal' | 'vertical
 * Controls the `flex-direction` property and maps to 'row' and 'column'
 *
 * @param primaryAlign: 'start' | 'end' | 'center' | 'space-around' | 'space-between'
 * Controls the `justify-content` property, and aligns the content of the container
 * on the 'primary' axis, the one that is parallel to the flex direction.
 *
 * @param secondaryAlign: 'start' | 'end' | 'center' | 'space-around' | 'space-between'
 * Controls the `align-items` property, and aligns the content of the container
 *
 * @param textAlign: 'left' | 'center' | 'right'
 * Controls the `text-align` property, and aligns the text of the container.
 *
 * @param id: string
 * Used to apply a custom ID property to the HTML button element
 *
 * @param className: string
 * Used to apply custom classes to the HTML button element
 *
 * @return HTML <div/> element containing provided elements, flex aligned.
 */
const FlexContainer = ({ children, id, className, direction = 'horizontal', primaryAlign = 'center', secondaryAlign = 'center', textAlign = 'center', fullWidth = false, }) => {
    // add default classes to optional custom className
    className = `${className ? className + ' ' : ''}izo-flex-container ${direction} primary-${primaryAlign} secondary-${secondaryAlign} text-${textAlign}${fullWidth ? ' full-width' : ''}`;
    // pass className and id to all possible return elements
    const defaultProps = { className, id };
    return React__default.createElement("div", Object.assign({}, defaultProps), children);
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

process.env.REACT_APP_EMAILOPTIN_URL ||
    process.env.NEXT_PUBLIC_EMAILOPTIN_URL ||
    '';
const LocalStorage = typeof window !== 'undefined' ? window.localStorage : undefined;

var _path$1;

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var SvgEmailOptIn = function SvgEmailOptIn(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    id: "EmailOptIn-icon",
    width: 108,
    height: 118,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    d: "M96.667 32H43.334v10.667h53.333v64H11.334v-64H22V64h10.667V21.333h32V0H22v32H11.334C5.467 32 .667 36.8.667 42.667v64c0 5.866 4.8 10.666 10.667 10.666h85.333c5.867 0 10.667-4.8 10.667-10.666v-64c0-5.867-4.8-10.667-10.667-10.667Z",
    fill: "current"
  })));
};

const UTM_MEDIUM_KEY = 'utm_medium';
const UTM_MEDIUM_EMAIL = 'email';
const LOCAL_STORAGE_KEY = 'izocloud_emailOptIn';
const EmailOptIn = ({ redirect, optInUrl }) => {
    const [optedIn, setOptedIn] = useState(false);
    const { user, getCreds } = useAuth();
    const { mixpanelEvent } = useAnalytics();
    useEffect(() => {
        if (mixpanelEvent) {
            mixpanelEvent(events.optInPageLoad);
        }
    }, [mixpanelEvent]);
    useEffect(() => {
        var _a;
        const sp = new URLSearchParams(window.location.search);
        if (typeof LocalStorage !== 'undefined' &&
            ((_a = sp.get(UTM_MEDIUM_KEY)) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === UTM_MEDIUM_EMAIL) {
            LocalStorage.setItem(LOCAL_STORAGE_KEY, 'true');
            redirect(true);
        }
    }, []);
    const setPreferences = () => __awaiter(void 0, void 0, void 0, function* () {
        if (typeof window === 'undefined' || typeof LocalStorage === 'undefined')
            return;
        if (optedIn && user) {
            if (!user.email) {
                console.error('Empty email in user object. Not calling opt-in endpoint.');
                return;
            }
            const payload = {
                user_id: user.sub,
                email: user.email,
                timestamp: Date.now(),
                url: window.location.href,
            };
            const { accessToken } = yield getCreds();
            fetch(optInUrl, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            }).then(res => {
                if (res.status !== 200) {
                    console.error('Got non-200 status code from email opt-in endpoint');
                }
            });
            if (mixpanelEvent)
                mixpanelEvent(events.optIn);
        }
        else {
            if (mixpanelEvent)
                mixpanelEvent(events.continueWithoutOptIn);
        }
        LocalStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(optedIn));
        redirect(true);
    });
    return (React__default.createElement(FlexContainer, { id: 'EmailOptIn', direction: 'vertical' },
        React__default.createElement(FlexContainer, { id: 'EmailOptIn-image', direction: 'vertical', primaryAlign: 'center' },
            React__default.createElement(SvgEmailOptIn, null)),
        React__default.createElement(Spacer, { height: 32 }),
        React__default.createElement(Text, { type: 'secondary-header', color: 'white', align: 'center', id: 'EmailOptIn-header' }, "Get the latest iZotope News and updates"),
        React__default.createElement(Spacer, { height: 16 }),
        React__default.createElement(FlexContainer, { id: 'EmailOptIn-checkbox', secondaryAlign: 'start' },
            React__default.createElement("input", { id: 'EmailOptIn-checkbox-input', type: 'checkbox', checked: optedIn, onChange: () => setOptedIn(!optedIn) }),
            React__default.createElement(Spacer, { width: 8 }),
            React__default.createElement(Text, { type: 'small', color: 'grey' }, "We occasionally offer special discounts and promotions to customers who have downloaded our products. Check here to be included in these offers.")),
        React__default.createElement(Spacer, { height: 32 }),
        React__default.createElement(Button, { id: 'EmailOptIn-button', type: 'solid', onClick: setPreferences }, "Continue"),
        React__default.createElement(Spacer, { height: 8 }),
        React__default.createElement(Text, { type: 'small', color: 'grey', align: 'center', id: 'EmailOptIn-fine-print' },
            "By clicking \u201CContinue\u201D you agree to our\u00A0",
            React__default.createElement(Button, { type: 'text', size: 'small' },
                React__default.createElement(ExternalLink, { to: 'https://www.izotope.com/en/privacy-policy.html' }, "Privacy Policy")),
            "\u00A0and\u00A0",
            React__default.createElement(Button, { type: 'text', size: 'small' },
                React__default.createElement(ExternalLink, { to: 'https://www.izotope.com/en/terms-of-use.html' }, "Terms of Service")))));
};

const DefaultRight = () => {
    const { user, logout } = useAuth();
    const { mixpanelEvent } = useAnalytics();
    const logOut = () => {
        if (mixpanelEvent)
            mixpanelEvent(events.logoutButtonClick);
        logout({ returnTo: window.location.origin });
    };
    const handleIconClickMobile = () => {
        if (window.confirm('Do you want to sign out?')) {
            logOut();
        }
    };
    return (React__default.createElement(FlexContainer, { direction: "horizontal" },
        window.innerWidth < 600 ? (React__default.createElement(Button, { type: 'text', onClick: handleIconClickMobile },
            React__default.createElement(Image, { src: user === null || user === void 0 ? void 0 : user.picture, alt: 'Ozone Logo' }))) : (React__default.createElement(Image, { src: user === null || user === void 0 ? void 0 : user.picture, alt: 'Ozone Logo' })),
        React__default.createElement(Spacer, { width: 8 }),
        React__default.createElement("div", { id: 'Header-right-content' },
            React__default.createElement(Text, { type: 'paragraph', color: 'white' }, user === null || user === void 0 ? void 0 : user.name),
            React__default.createElement(Button, { type: 'text', size: 'small', color: 'grey', onClick: logOut, id: 'SignOut-Button' }, "Sign Out"))));
};
const Header = ({ left, right, center }) => {
    return (React__default.createElement("header", { id: 'Header' },
        React__default.createElement("div", { id: 'Header-left' }, left),
        center ? React__default.createElement("div", { id: 'Header-center' }, center) : null,
        right ? React__default.createElement("div", { id: 'Header-right' }, right) : null));
};

var labsUrl = "data:image/svg+xml,%3Csvg%20width%3D%22134%22%20height%3D%2254%22%20viewBox%3D%220%200%20134%2054%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M76.9545%2036.8784V33.0685H70.2006V16.5301H66.2176V36.8784H76.9545ZM83.4227%2037.1382C85.8183%2037.1382%2087.0594%2035.6373%2087.2903%2035.0889V36.8784H90.8693V20.6575H87.2903V22.4759C87.0594%2021.9564%2085.8183%2020.4555%2083.4227%2020.4555C80.5653%2020.4555%2078.5738%2022.245%2078.5738%2026.4301V31.1347C78.5738%2035.291%2080.5076%2037.1382%2083.4227%2037.1382ZM84.7504%2033.7612C83.3361%2033.7612%2082.4125%2032.8665%2082.4125%2030.8461V26.7187C82.4125%2024.7272%2083.3361%2023.8324%2084.7504%2023.8324C86.0781%2023.8324%2087.0594%2024.6983%2087.0594%2026.661V30.8461C87.0594%2032.8376%2086.1358%2033.7612%2084.7504%2033.7612ZM101.448%2037.1382C104.392%2037.1382%20106.326%2035.3198%20106.326%2031.1636V26.4301C106.326%2022.3027%20104.392%2020.4844%20101.448%2020.4844C98.9085%2020.4844%2097.8117%2022.1295%2097.8117%2022.1295V15.087H94.0307V36.8784H97.5808V35.1178C97.8117%2035.6662%2099.0817%2037.1382%20101.448%2037.1382ZM100.15%2033.7901C98.7642%2033.7901%2097.8117%2032.8665%2097.8117%2030.875V26.661C97.8117%2024.7272%2098.7931%2023.8613%20100.15%2023.8613C101.564%2023.8613%20102.487%2024.7849%20102.487%2026.7476V30.875C102.487%2032.8665%20101.535%2033.7901%20100.15%2033.7901ZM114.423%2020.4555C110.815%2020.4555%20108.651%2022.2738%20108.651%2025.2467C108.651%2030.9615%20116.905%2029.4318%20116.905%2032.2604C116.905%2033.184%20116.068%2033.9921%20114.596%2033.9921C113.153%2033.9921%20112.085%2033.2417%20112.085%2031.4522H108.275C108.275%2035.0601%20110.7%2037.1959%20114.596%2037.1959C118.579%2037.1959%20120.744%2035.4353%20120.744%2032.2892C120.744%2026.661%20112.403%2028.2773%20112.403%2025.2467C112.403%2024.2654%20113.153%2023.6881%20114.452%2023.6881C115.866%2023.6881%20116.848%2024.1788%20116.848%2025.9971H120.571C120.571%2022.3027%20118.377%2020.4555%20114.423%2020.4555Z%22%20fill%3D%22%23E6E6E6%22%2F%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M26.8949%2053.7897C41.7485%2053.7897%2053.7897%2041.7485%2053.7897%2026.8949C53.7897%2012.0412%2041.7485%200%2026.8949%200C12.0412%200%200%2012.0412%200%2026.8949C0%2041.7485%2012.0412%2053.7897%2026.8949%2053.7897ZM35.6751%2027.2476C39.3127%2027.4242%2042.3446%2027.1249%2042.8538%2025.9299C43.5117%2024.3836%2036.0711%2017.8853%2028.1323%2012.2376C28.4726%2012.8047%2028.7962%2013.3812%2029.1197%2013.9576C29.1964%2014.0942%2029.2731%2014.2308%2029.3499%2014.3673C30.0276%2015.5859%2030.6933%2016.8111%2031.3439%2018.0447C32.6425%2020.5141%2033.8943%2023.01%2035.0024%2025.5845C35.236%2026.1337%2035.459%2026.6888%2035.6751%2027.2476ZM35.7375%2031.7556C35.7227%2031.1373%2035.5958%2030.472%2035.4375%2029.819C35.3708%2029.5477%2035.288%2029.2778%2035.2052%2029.0079C35.1743%2028.907%2035.1433%2028.8061%2035.1132%2028.7051C32.9862%2028.5822%2030.8753%2028.3077%2029.2303%2028.0492C29.2288%2028.0523%2029.2271%2028.055%2029.2255%2028.058C29.2248%2028.0592%2029.2241%2028.0605%2029.2234%2028.0618C29.2337%2028.0711%2029.245%2028.0788%2029.2562%2028.0865C29.2725%2028.0976%2029.2888%2028.1088%2029.302%2028.1247C29.3063%2028.1296%2029.3105%2028.1348%2029.3144%2028.1399L29.3241%2028.1522C30.1391%2029.1909%2031.109%2030.1766%2032.1339%2031.0477C32.6494%2031.4794%2033.1855%2031.8892%2033.742%2032.2206C34.2767%2032.5448%2034.9255%2032.8212%2035.3042%2032.7698C35.396%2032.7611%2035.4523%2032.7393%2035.5042%2032.7007C35.5569%2032.6664%2035.5847%2032.6384%2035.6276%2032.5397C35.7117%2032.3627%2035.7442%2032.0584%2035.7375%2031.7556ZM17.8661%2022.0339C17.8809%2022.6525%2018.008%2023.3178%2018.1663%2023.9708C18.2334%2024.2439%2018.3168%2024.5158%2018.4002%2024.7876C18.4305%2024.8863%2018.4608%2024.985%2018.4902%2025.0837C20.6157%2025.2065%2022.7265%2025.482%2024.3711%2025.74C24.3735%2025.7355%2024.3763%2025.7313%2024.3786%2025.7268C24.3684%2025.7174%2024.3572%2025.7097%2024.346%2025.702C24.3302%2025.6912%2024.3145%2025.6804%2024.3016%2025.6651C24.2975%2025.66%2024.2934%2025.6549%2024.2894%2025.6499L24.2797%2025.6378C23.4647%2024.5989%2022.4948%2023.6133%2021.4698%2022.742C20.9542%2022.3102%2020.4183%2021.9006%2019.8619%2021.5692C19.3272%2021.2448%2018.6784%2020.9685%2018.2996%2021.0199C18.2076%2021.0287%2018.1515%2021.0503%2018.0996%2021.0892C18.0467%2021.1233%2018.0191%2021.1514%2017.9761%2021.2501C17.8922%2021.427%2017.8596%2021.7314%2017.8661%2022.0339ZM22.2597%2035.745C20.9611%2033.2757%2019.7093%2030.7798%2018.6014%2028.2053C18.3677%2027.6557%2018.1444%2027.1002%2017.9281%2026.5412C14.2903%2026.3644%2011.2583%2026.6641%2010.7496%2027.8599C10.2113%2029.126%2015.9493%2034.8097%2025.4759%2041.5591C25.1308%2040.9847%2024.8031%2040.4006%2024.4754%2039.8167C24.4016%2039.6852%2024.3279%2039.5537%2024.2539%2039.4223C23.5761%2038.2041%2022.9103%2036.9788%2022.2597%2035.745ZM28.6812%2010.3792C28.6812%2010.5183%2028.663%2010.6524%2028.6336%2010.7827C34.1436%2014.664%2045.6111%2023.2461%2044.226%2026.4982C43.4845%2028.2417%2040.5484%2028.7723%2037.2495%2028.7723C36.9037%2028.7723%2036.5533%2028.7647%2036.2014%2028.7538C36.2346%2028.8548%2036.2699%2028.9548%2036.3051%2029.0548C36.363%2029.219%2036.4209%2029.3832%2036.47%2029.5522C36.6652%2030.2407%2036.8377%2030.9414%2036.8906%2031.7111C36.9072%2032.0981%2036.9147%2032.5058%2036.739%2032.9782C36.5916%2033.4698%2036.0105%2033.932%2035.4926%2033.9754C34.9651%2034.0556%2034.5382%2033.937%2034.1515%2033.8132C33.7654%2033.6812%2033.4188%2033.5114%2033.0878%2033.3284C32.4276%2032.9604%2031.8352%2032.5319%2031.2646%2032.0827C30.163%2031.199%2029.1571%2030.2411%2028.2347%2029.1515C27.8178%2029.4101%2027.3283%2029.5657%2026.7997%2029.5657C25.4078%2029.5657%2024.2746%2028.525%2024.124%2027.1915C22.4377%2026.9318%2020.6666%2026.7248%2018.9776%2026.6057C19.005%2026.6837%2019.0323%2026.7617%2019.0595%2026.8397C19.1809%2027.1868%2019.3023%2027.5339%2019.4324%2027.8784C20.4185%2030.4593%2021.561%2032.9945%2022.7518%2035.5048C23.3489%2036.7598%2023.9619%2038.0082%2024.5873%2039.2506C24.7139%2039.5049%2024.8412%2039.7588%2024.9686%2040.0127C25.2692%2040.6118%2025.5699%2041.2111%2025.8606%2041.8159C26.1377%2041.6567%2026.4557%2041.5589%2026.7997%2041.5589C27.8364%2041.5589%2028.6769%2042.388%2028.6769%2043.4105C28.6769%2044.4333%2027.8364%2045.2621%2026.7997%2045.2621C25.7629%2045.2621%2024.9224%2044.4333%2024.9224%2043.4105C24.9224%2043.2715%2024.9408%2043.1372%2024.97%2043.0071C19.461%2039.1258%207.99392%2030.5436%209.37719%2027.2916C10.1967%2025.3654%2013.6958%2024.9215%2017.4023%2025.0362C17.369%2024.9351%2017.3338%2024.8351%2017.2986%2024.7351C17.2407%2024.5709%2017.1829%2024.4066%2017.1338%2024.2375C16.9386%2023.5489%2016.7661%2022.8484%2016.7132%2022.0787C16.6966%2021.6915%2016.6889%2021.2841%2016.8648%2020.8116C17.012%2020.32%2017.5933%2019.8578%2018.1113%2019.8144C18.6385%2019.7339%2019.0654%2019.8527%2019.4523%2019.9765C19.8382%2020.1087%2020.185%2020.2781%2020.5158%2020.4613C21.1761%2020.8293%2021.7684%2021.2579%2022.3392%2021.707C23.441%2022.5912%2024.4473%2023.5493%2025.3699%2024.639C25.7856%2024.3824%2026.2731%2024.2278%2026.7997%2024.2278C28.1899%2024.2278%2029.3219%2025.2659%2029.4747%2026.5969C31.1623%2026.8566%2032.9352%2027.064%2034.6256%2027.1831C34.599%2027.1071%2034.5724%2027.031%2034.5458%2026.9549C34.4239%2026.606%2034.302%2026.2574%2034.1713%2025.9112C33.1851%2023.3304%2032.0429%2020.7953%2030.8518%2018.2848C30.255%2017.03%2029.6419%2015.7816%2029.0164%2014.5392C28.8874%2014.2799%2028.7575%2014.021%2028.6277%2013.7621C28.3296%2013.1679%2028.0316%2012.5736%2027.7432%2011.9736C27.4661%2012.1331%2027.1479%2012.2308%2026.8041%2012.2308C25.7674%2012.2308%2024.9268%2011.4018%2024.9268%2010.3792C24.9268%209.35649%2025.7674%208.52765%2026.8041%208.52765C27.8407%208.52765%2028.6812%209.35649%2028.6812%2010.3792Z%22%20fill%3D%22%23E6E6E6%22%2F%3E%3C%2Fsvg%3E";

const Footer = () => {
    return (React__default.createElement("footer", { id: 'Footer' },
        React__default.createElement("div", { id: 'Footer-left' },
            React__default.createElement(Image, { src: labsUrl, alt: 'iZotope Labs Logo' })),
        React__default.createElement("div", { id: 'Footer-right' },
            React__default.createElement(Text, { type: 'small', color: 'white' },
                "\u00A9 ",
                new Date().getFullYear(),
                " iZotope, Inc."),
            React__default.createElement(Spacer, { width: 8 }),
            React__default.createElement(Button, { type: 'text', size: 'small' },
                React__default.createElement(ExternalLink, { to: 'https://www.izotope.com/en/terms-of-use.html' }, "Terms of Service")),
            React__default.createElement(Spacer, { width: 8 }),
            React__default.createElement(Button, { type: 'text', size: 'small' },
                React__default.createElement(ExternalLink, { to: 'https://www.izotope.com/en/privacy-policy.html' }, "Privacy Policy")))));
};

var _path;

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var SvgPerson = function SvgPerson(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    id: "Login-svg",
    width: 40,
    height: 47,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M4.397 42.06h31.205v-.795c0-2.809-1.503-5.418-4.028-6.581a24.822 24.822 0 0 0-11.575-2.468c-4.936 0-8.028.822-11.574 2.468a7.177 7.177 0 0 0-4.028 6.581v.794ZM.14 41.321a11.347 11.347 0 0 1 6.156-10.497A30.07 30.07 0 0 1 20 27.99c5.617 0 9.589.964 13.702 2.893 3.894 2.001 6.29 6.064 6.156 10.44v4.993H.141v-4.993ZM20 21.35c2.1 0 5.674-2.837 5.674-7.688 0-6.24-1.759-8.51-5.674-8.51-3.915 0-6.24 2.27-6.24 8.51 0 4.823 4.141 7.66 6.24 7.66v.028Zm0 4.256c-4.34 0-10.496-4.483-10.496-11.944C9.503 6.202 12.737.925 20 .925c7.263 0 9.93 5.305 9.93 12.766 0 7.46-5.59 11.915-9.93 11.915Z",
    fill: "current"
  })));
};

const Login = ({ type = 'redirect', redirectLoginOptions = undefined, popupLoginOptions = undefined, }) => {
    const { loginWithRedirect, loginWithPopup } = useAuth();
    const { mixpanelEvent } = useAnalytics();
    useEffect(() => {
        if (mixpanelEvent) {
            mixpanelEvent(events.loginPageLoad);
        }
    }, [mixpanelEvent]);
    const login = () => {
        if (type === 'popup')
            loginWithPopup(popupLoginOptions);
        else
            loginWithRedirect(redirectLoginOptions);
        mixpanelEvent(events.loginButtonClick);
    };
    return (React__default.createElement(FlexContainer, { id: 'Login', direction: 'vertical' },
        React__default.createElement(FlexContainer, { id: 'Login-image', direction: 'vertical', primaryAlign: 'center' },
            React__default.createElement(SvgPerson, null)),
        React__default.createElement(Spacer, { height: 32 }),
        React__default.createElement(Text, { type: 'section-header', color: 'white', id: 'HeaderText' }, "Sign In"),
        React__default.createElement(Text, { type: 'sub-header', align: 'center', color: 'grey', id: 'SubText' }, "with your iZotope or Google account"),
        React__default.createElement(Spacer, { height: 32 }),
        React__default.createElement(Button, { id: 'Login-button', type: 'solid', onClick: login }, "Continue")));
};

var _g;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var SvgOops = function SvgOops(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    id: "Crash-oops-svg",
    width: 497,
    height: 247,
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    id: "Iteration",
    stroke: "none",
    strokeWidth: 1,
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("g", {
    id: "Group-10-Copy",
    transform: "translate(0 1)"
  }, /*#__PURE__*/React.createElement("g", {
    id: "Group-9",
    transform: "translate(20)"
  }, /*#__PURE__*/React.createElement("circle", {
    id: "Oval",
    fill: "current",
    opacity: 0.086,
    transform: "rotate(-10 373.537 99.123)",
    cx: 373.537,
    cy: 99.123,
    r: 70.5
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-12",
    fill: "current",
    opacity: 0.07,
    transform: "rotate(-10 139.471 128.74)",
    cx: 139.471,
    cy: 128.739,
    r: 70.5
  }), /*#__PURE__*/React.createElement("g", {
    id: "Group-6",
    opacity: 0.637,
    transform: "rotate(-10 368.169 28.274)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M40.779 55.9c7.066-1.954 14.905-3.356 23.515-4.206 27.994-2.762 29.083-12.832 31.825-16.686 2.741-3.854 20.853-25.584 30.153-30.34 9.3-4.755 14.768-5.942 21.562-3.263 6.794 2.679.66 11.332-5.378 13.711-6.037 2.38-13.444 12.03-7.183 22.957 4.175 7.285 8.362 9.562 12.561 6.83l55.847-34.841v3.703l1.436 3.308v2.01l-.844 3.61 2.295 1.404-.363 4.39.363 2.44v.756l-64.112 42.5c-.892 2.921-.892 5.476 0 7.663 1.34 3.281 3.816 10.005 10.298 11.84 4.321 1.223 9.845.432 16.572-2.375 3.817-1.66 6.63-1.475 8.438.553 2.712 3.041 1.343 8.317-.976 11.398-2.319 3.08-5.27 7.019-16.264 11.37-10.994 4.35-25.186 3.75-32.321 11.872-4.758 5.414-8.993 11.63-12.708 18.648-8.214 14.574-18.607 25.456-31.18 32.646-18.86 10.784-37.445 3.127-48.992-10.14-11.547-13.266-24.863-33.771-33.248-59.43-5.59-17.107.115-31.517 17.116-43.23 6.16-3.326 11.043-5.63 14.651-6.915",
    id: "Path",
    stroke: "current",
    strokeWidth: 0.5
  }), /*#__PURE__*/React.createElement("path", {
    d: "m76.784 127.023 1.966 2.978c4.334 5.985 5.417 13.897 3.25 23.736-2.167 9.84-1.04 15.188 3.379 16.044 3.723.722 7.153-1.254 10.291-5.93 4.707-7.013 10.37-13.538 17.885-25.717 7.514-12.18 12.571-18.777 22.822-21.458 10.25-2.682 30.466-5.76 36.857-15.49 4.261-6.487 3.186-7.965-3.224-4.434-6.043 3.94-12.5 4.426-19.372 1.455-6.657-2.878-10.856-9.372-12.595-19.484-.056-.326-.122-.568-.167-.633-.045-.065-.5 0-.753.177l-2.449 1.722c-3.52-2.933-6.701-6.652-8.539-9.628-1.837-2.975-3-6.09-3.49-9.345l9.922-6.786c.8-3.83.554-6.47-.74-7.922-1.292-1.453-2.812-1.91-4.559-1.373-7.372 6.92-12.525 11.331-15.458 13.231-4.401 2.85-13.445 9.203-35.683 12.195-14.825 1.995-22.102 9.027-21.828 21.094l3.46 6.752.321.486",
    id: "Path",
    stroke: "current",
    strokeWidth: 0.5
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path",
    stroke: "current",
    strokeWidth: 0.5,
    d: "m79.188 125.46-10.357 6.725-18.463-28.43 14.09-9.15 17.898 27.56"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Rectangle-Copy-71",
    stroke: "current",
    strokeWidth: 0.5,
    d: "m50.26 103.782 18.464 28.431-2.768 1.797-18.463-28.43z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m72.062 125.173 28.829-8.488 23.42-14.218a1.377 1.377 0 0 1 1.806.338 1.111 1.111 0 0 1-.299 1.623l-23.511 14.468-29.33 8.343a1.295 1.295 0 0 1-1.538-.72.986.986 0 0 1 .623-1.346Z",
    id: "Path-15",
    stroke: "current",
    strokeWidth: 0.5
  }), /*#__PURE__*/React.createElement("rect", {
    id: "Rectangle",
    stroke: "current",
    strokeWidth: 0.5,
    transform: "rotate(-25 81.849 101.765)",
    x: 77.799,
    y: 87.515,
    width: 8.1,
    height: 28.5,
    rx: 4.05
  }), /*#__PURE__*/React.createElement("rect", {
    id: "Rectangle",
    stroke: "current",
    strokeWidth: 0.5,
    transform: "rotate(-33 100.708 88.896)",
    x: 96.658,
    y: 74.646,
    width: 8.1,
    height: 28.5,
    rx: 4.05
  }), /*#__PURE__*/React.createElement("rect", {
    id: "Rectangle",
    stroke: "current",
    strokeWidth: 0.5,
    transform: "rotate(-34 121.108 75.696)",
    x: 117.058,
    y: 61.446,
    width: 8.1,
    height: 28.5,
    rx: 4.05
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path",
    stroke: "current",
    strokeWidth: 0.5,
    d: "m54.982 106.004 58.553-36.831 90.097-56.674M57.058 109.605l58.199-37.068 89.277-56.862M59.158 113.505l57.49-37.186 88.468-57.223"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-16-Copy-3",
    stroke: "current",
    strokeWidth: 0.5,
    d: "m61.558 117.105 56.546-36.95 87.154-56.95"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path",
    stroke: "current",
    strokeWidth: 0.5,
    d: "m63.358 120.705 57.018-37.422 85.955-56.414M65.458 124.905l56.074-37.54 85.02-56.92"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-14",
    stroke: "current",
    strokeWidth: 0.5,
    d: "m33.518 58.2 1.564 11.955 5.7-1.22-3.466 13.238L21.99 98.659l15.327 1.698-11.59-3.114 14.017-13.64 5.305-18.361-6.66.668 2.666-10.272"
  }), /*#__PURE__*/React.createElement("g", {
    id: "Group-4",
    transform: "translate(37 146)"
  }, /*#__PURE__*/React.createElement("rect", {
    id: "Rectangle",
    stroke: "current",
    strokeWidth: 0.5,
    x: 0,
    y: 0,
    width: 39.6,
    height: 13.2,
    rx: 6.6
  }), /*#__PURE__*/React.createElement("rect", {
    id: "Rectangle",
    stroke: "current",
    strokeWidth: 0.5,
    opacity: 0.215,
    x: 10.8,
    y: 2.4,
    width: 18.6,
    height: 8.4,
    rx: 0.6
  }), /*#__PURE__*/React.createElement("g", {
    id: "Group-2",
    transform: "translate(31.8 2.4)",
    fill: "current"
  }, /*#__PURE__*/React.createElement("circle", {
    id: "Oval",
    cx: 0.3,
    cy: 8.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-6",
    cx: 2.1,
    cy: 8.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-3",
    cx: 0.3,
    cy: 5.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-7",
    cx: 2.1,
    cy: 5.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-10",
    cx: 3.9,
    cy: 5.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-4",
    cx: 0.3,
    cy: 0.3,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-8",
    cx: 2.1,
    cy: 0.3,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-5",
    cx: 0.3,
    cy: 2.7,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-9",
    cx: 2.1,
    cy: 2.7,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-11",
    cx: 3.9,
    cy: 2.7,
    r: 1
  })), /*#__PURE__*/React.createElement("g", {
    id: "Group-2-Copy",
    transform: "rotate(-180 4.2 5.4)",
    fill: "current"
  }, /*#__PURE__*/React.createElement("circle", {
    id: "Oval",
    cx: 0.3,
    cy: 8.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-6",
    cx: 2.1,
    cy: 8.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-3",
    cx: 0.3,
    cy: 5.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-7",
    cx: 2.1,
    cy: 5.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-10",
    cx: 3.9,
    cy: 5.1,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-4",
    cx: 0.3,
    cy: 0.3,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-8",
    cx: 2.1,
    cy: 0.3,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-5",
    cx: 0.3,
    cy: 2.7,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-9",
    cx: 2.1,
    cy: 2.7,
    r: 1
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-11",
    cx: 3.9,
    cy: 2.7,
    r: 1
  })))), /*#__PURE__*/React.createElement("g", {
    id: "Group-5",
    opacity: 0.533,
    transform: "rotate(8 110.435 1624.139)",
    stroke: "current",
    strokeWidth: 0.5
  }, /*#__PURE__*/React.createElement("path", {
    d: "m.972 59.952-.383-2.535 1.356-4.477L0 51.396l1.945-3.61v-2.06L.589 43.098l2.417-4.018 87.741-25.073 3.772-5.72c.809-1.086 2.293-1.63 4.454-1.63h1.868V4.544c-1.675-.349-2.405-1.262-2.19-2.74.214-1.478 1.392-2.048 3.533-1.71 1.86.233 2.648 1.032 2.364 2.4-.284 1.367-1.174 2.05-2.67 2.05v2.114h7.745V4.544c-1.488-.224-2.231-.908-2.231-2.05 0-1.715.907-2.4 2.834-2.4s2.922.772 2.922 2.4c0 1.627-1.907 2.05-2.414 2.05-.339 0-.339.705 0 2.114h7.222V5.14h-.535l.878-2.256-.343-2.272h2.37l-.746 1.881.466 2.647h-.858v1.518h7.444V5.14a7.7 7.7 0 0 1-.617-3c0-1.019.488-1.528 1.463-1.528.78.16 1.107.917.982 2.272-.126 1.356-.453 2.108-.982 2.256v1.518h6.965V5.14l-.39-.596.64-1.217-.25-2.715h2.277l-.586 2.272.586 2.256-1.037.43v1.325h7.707V5.14c-1.35-.51-1.878-1.392-1.588-2.647.29-1.254 1.399-1.7 3.326-1.34 1.23.24 1.844.965 1.844 2.174 0 1.208-.811 1.813-2.434 1.813v1.755c4.933-.42 8.348 1.254 10.247 5.02 1.9 3.765 1.467 7.82-1.298 12.163-1.252 1.785-3.424 2.538-6.515 2.259-3.09-.28-5.16-1.547-6.207-3.803-.918-1.766-2.267-2.208-4.045-1.325-1.779.883-8.569 4.614-20.37 11.194-1.198.82-2.707 1.4-4.528 1.74-1.82.34-2.943-.577-3.369-2.75-.323-1.526-1.317-2.29-2.984-2.29h-9.033l-7.15 2.29L.972 59.953Z",
    id: "Path-13"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path",
    d: "m1.444 41.569 98.833-28.63M1.444 45.119l108.174-31.806"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-19",
    d: "m1.444 48.719 117.191-35.485"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path",
    d: "m1.066 52.173 125.425-39.107"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-21",
    d: "m1.066 56.105 134.271-42.871"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-22",
    d: "m.838 59.219 143.406-45.985"
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-18",
    cx: 136.283,
    cy: 11.661,
    r: 1.8
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-19",
    cx: 127.883,
    cy: 11.961,
    r: 1.8
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-20",
    cx: 119.783,
    cy: 11.961,
    r: 1.8
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-21",
    cx: 110.783,
    cy: 11.961,
    r: 1.8
  }), /*#__PURE__*/React.createElement("circle", {
    id: "Oval-Copy-22",
    cx: 101.783,
    cy: 11.961,
    r: 1.8
  })), /*#__PURE__*/React.createElement("g", {
    id: "Group-7",
    opacity: 0.636,
    transform: "rotate(-10 516.69 -1261.004)",
    stroke: "current"
  }, /*#__PURE__*/React.createElement("g", {
    id: "Group-8",
    strokeWidth: 0.525
  }, /*#__PURE__*/React.createElement("path", {
    d: "M15.285 38.923a12.657 12.657 0 0 0-.516 3.558c0 16.624 33.377 30.1 74.55 30.1 41.173 0 74.55-13.476 74.55-30.1 0-16.624-33.377-30.1-74.55-30.1-13.712 0-26.56 1.495-37.597 4.103m-5.418 1.41c-12.814 3.661-22.64 8.914-27.711 15.046",
    id: "Shape"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.37 45.981c0 12.067 12.736 22.735 32.234 29.197m2.803.886c11.808 3.556 25.848 5.617 40.912 5.617 41.946 0 75.95-15.983 75.95-35.7",
    id: "Shape"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11.27 48.781c0 21.263 35.1 38.5 78.4 38.5 18.748 0 35.96-3.232 49.45-8.623m3.295-1.392c15.761-7.043 25.654-17.196 25.654-28.485v-2.8",
    id: "Shape"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M165.27 37.548c.466-.425.933-.637 1.4-.637.466 0 .933.212 1.4.637v12.186c-.467.298-.934.447-1.4.447-.467 0-.934-.149-1.4-.447V37.548Z",
    id: "Rectangle"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path",
    d: "m56.285 15.521 17.632 6.972 15.402 2.374 28.58-1.452 1.072.683 18.944-.924 6.333-1.044"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m27.33 58.652 22.745-2.29h16.557l17.095 2.29 3.874-1.145 3.468 1.145h4.2l37.725-1.145c16.847-2.778 25.393-4.168 25.64-4.168.246 0-.45.737-2.088 2.21C140.612 66.903 118.32 72.58 89.669 72.58c-28.65 0-49.43-4.643-62.339-13.929Z",
    id: "Path-17"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11.27 48.88c0-4.386.269-5.606.803-7.371.426-1.41 1.207-2.701 1.657-3.645m3.751-5.774C22.9 25.66 31.862 20.53 44.369 16.694c.362-.111.727-.22 1.094-.328m5.378-1.41c32.786-7.655 78.903-2.456 93.407 7.174M13.37 46.12c0-2.043.139-3.082.298-3.883.26-1.319.738-2.408 1.268-3.658",
    id: "Shape"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M41.039 57.18 1.124 28.837c-1.499-1.44-1.499-2.953 0-4.542 1.498-1.59 3.052-1.695 4.662-.319l45.858 32.302-10.605.902Z",
    id: "Path-23"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M67.767 56.198 38.23 3.413C37.4 1.855 37.757.807 39.296.268c1.54-.54 2.658-.264 3.355.825L75.757 57.18l-7.99-.982Z",
    id: "Path-24"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M17.015 70.692 19.4 95.784c-5.83.819-9.342-2.612-10.537-10.29-1.194-7.68-1.638-12.613-1.332-14.802l1.332-1.454-.647-14.157m-.117-2.556L7.53 40.099",
    id: "Shape"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m17.27 71.009-6-1.525-.527-10.55 4.655 4.897v3.512c10.198 8.883 20.32 14.603 30.367 17.16 13.319 3.389 55.12 14.807 92.482.463a92.501 92.501 0 0 0 3.182-1.29 88.161 88.161 0 0 0 11.249-5.78c13.46-11.541 18.535-21.603 15.391-30.022m-2.8-4.916a84.424 84.424 0 0 0-1.4-1.4",
    id: "Shape"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.53 40.28c.274-.56.64-.85 1.097-.875.457-.025.915.207 1.374.694l.538 13.72c-1.627.177-2.44-.307-2.44-1.453",
    id: "Path"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m49.569 136.281-1.184-23.773 3.931-3.717c4.21 8.285 4.21 16.764 0 25.436-.756 1.37-1.672 2.054-2.747 2.054Z",
    id: "Path-28"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M48.385 112.508c-2.696.815-4.29.815-4.781 0-.738-1.224-4.938 12.114 2.39 23.773.78.203 1.971.203 3.575 0",
    id: "Path-29"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M52.532 133.783c3.18-.28 4.77-.979 4.77-2.097 0-.052 0-.12-.002-.202-.014-1.127-.254-9.195-.718-24.204-1.906-2.827-4.443-4.054-7.61-3.679m-2.59.653c-1.042.521-1.392 1.382-1.392 3.026v1.511l-1.78 3.869",
    id: "Shape"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M45.654 78.146v-4.219a3.032 3.032 0 0 1 1.292-.266c.48 0 .96.089 1.439.266l.095 5.1c-.745.11-1.294.11-1.645 0-.352-.109-.745-.403-1.181-.88Zm2.944 7.137.441 23.508c-.508.34-.96.51-1.359.51-.397 0-.795-.17-1.193-.51l-.578-24.176M52.336 108.62c-1.504-.274-2.58-.43-3.225-.466m-2.682.155c-.533.11-1.013.27-1.439.482",
    id: "Shape"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M127.616 133.43c-2.768.097-4.151-.378-4.151-1.425 0-1.047 1.005-8.785 3.016-23.214 1.894-4.148 4.874-5.566 8.94-4.255",
    id: "Path-33"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M127.506 133.442c-1.735-10.008-.435-18.187 3.9-24.537",
    id: "Path-34"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m127.506 133.442 4.292 2.794c2.961-13.241 4.168-21.177 3.622-23.807",
    id: "Path-35"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m135.554 112.562-4.024-3.77c1.364-1.263 2.569-1.695 3.615-1.298",
    id: "Path-36"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m132.028 136.433 2.892.52c4.379-6.606 5.983-15.027 4.814-25.261",
    id: "Path-37"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-38",
    d: "m139.734 111.766-4.433.46"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-39",
    d: "m139.82 111.914-2.27-2.722"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m138.233 84.893-3.414 24.245c.108.526.414.932.92 1.217.505.285 1.017.342 1.538.17l4.098-26.873m.365-3.038 1.119-6.69c-.532-.29-1.047-.46-1.544-.51a3.28 3.28 0 0 0-1.495.204l-1.017 7.228c.524.202.958.324 1.3.367.343.043.889-.157 1.637-.599Z",
    id: "Shape"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-41",
    d: "m147.28 116.048-3.032 4.115h-1.39l1.39-19.393 12.91-17.69h1.524v8.4"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-42",
    d: "m145.057 115.816.774-14.195 9.908-13.118v2.978h2.943v8.7l-7.912 15.635z"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-43",
    d: "M150.77 115.816V102.16h-4.837"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-44",
    d: "M150.77 102.16v-2.514h2.48v11.432"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-45",
    d: "m153.25 99.646 5.061-8.177h-1.996l-5.545 8.177"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M158.682 94.73c4.958-1.42 7.438-7 7.438-16.741",
    id: "Path-46"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-47",
    d: "M166.12 77.989v-5.855h-5.975l-2.68 2.228v8.103"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path-48",
    d: "M167.299 60.234 165.65 71.95l-2.552.185.77-5.95"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20.618 117.863c-1.365 3.116-3.049 4.913-3.049 8.268 0 22.23 31.81 40.25 71.05 40.25 39.24 0 71.05-18.02 71.05-40.25 0-4.087-.645-5.97-2.644-9.686",
    id: "Path"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m19.4 95.784 1.702 30.83c0 18.87 30.27 34.167 67.66 34.167s67.7-15.297 67.7-34.166l1.403-24.784",
    id: "Path"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10.743 58.934c-3.567-5.036-4.522-7.59-2.862-7.661",
    id: "Path-26"
  })), /*#__PURE__*/React.createElement("path", {
    id: "Path-16",
    strokeWidth: 0.53,
    d: "m100.083 160.188-6.87-15.404 7.465-3.37-11.643-7.972 10.595-9.409-4.774 8.868 15.103 6.956-6.71 8.324 15.42 5.204-4.164 4.771"
  }))), /*#__PURE__*/React.createElement("path", {
    d: "M185.64 151.7c9.31 0 14.91-5.95 14.91-15.61v-9.45c0-9.31-5.6-15.4-14.91-15.4-9.52 0-14.91 6.09-14.91 15.4v9.45c0 9.66 5.39 15.61 14.91 15.61Zm0-8.19c-3.22 0-5.67-1.89-5.67-6.09v-11.9c0-4.2 2.45-6.02 5.67-6.02 3.15 0 5.67 1.82 5.67 6.02v11.9c0 4.2-2.59 6.09-5.67 6.09Zm35.28 8.19c9.31 0 14.91-5.95 14.91-15.61v-9.45c0-9.31-5.6-15.4-14.91-15.4-9.52 0-14.91 6.09-14.91 15.4v9.45c0 9.66 5.39 15.61 14.91 15.61Zm0-8.19c-3.22 0-5.67-1.89-5.67-6.09v-11.9c0-4.2 2.45-6.02 5.67-6.02 3.15 0 5.67 1.82 5.67 6.02v11.9c0 4.2-2.59 6.09-5.67 6.09Zm30.87 21v-17.08s2.52 3.99 8.68 3.99c7.14 0 11.83-4.41 11.83-14.42v-11.48c0-10.08-4.69-14.49-11.83-14.49-5.74 0-8.82 3.57-9.38 4.9v-4.27h-8.61v52.85h9.31Zm5.53-21.28c-3.29 0-5.67-2.1-5.67-6.79v-10.22c0-4.83 2.31-7.07 5.67-7.07s5.67 2.24 5.67 7.07v10.01c0 4.76-2.24 7-5.67 7Zm35.42 8.54c9.66 0 14.91-4.27 14.91-11.9 0-13.65-20.23-9.73-20.23-17.08 0-2.38 1.82-3.78 4.97-3.78 3.43 0 5.81 1.19 5.81 5.6h9.03c0-8.96-5.32-13.44-14.91-13.44-8.75 0-14 4.41-14 11.62 0 13.86 20.02 10.15 20.02 17.01 0 2.24-2.03 4.2-5.6 4.2-3.5 0-6.09-1.82-6.09-6.16h-9.24c0 8.75 5.88 13.93 15.33 13.93Zm26.46-15.96 3.5-22.05c.42-2.8.84-5.11.84-7.14 0-3.08-2.03-5.46-5.53-5.46-3.43 0-5.53 2.38-5.53 5.46 0 2.03.49 4.34.91 7.14l3.43 22.05h2.38Zm-1.19 15.75c3.15 0 5.67-2.45 5.67-5.53s-2.52-5.6-5.67-5.6c-3.01 0-5.6 2.52-5.6 5.6 0 3.08 2.59 5.53 5.6 5.53Z",
    id: "oops",
    fill: "current",
    fillRule: "nonzero"
  })))));
};

const Crash = ({ children }) => {
    const { mixpanelEvent } = useAnalytics();
    useEffect(() => {
        if (mixpanelEvent) {
            mixpanelEvent(events.crashPageLoad);
        }
    }, [mixpanelEvent]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(FlexContainer, { id: 'Crash', direction: 'vertical' },
            React__default.createElement(FlexContainer, { id: 'Oops', direction: 'vertical' },
                React__default.createElement(SvgOops, null),
                React__default.createElement(Spacer, { height: 16 }),
                React__default.createElement(Text, { type: 'small', align: 'center', color: 'grey' }, "Looks like something went wrong"),
                React__default.createElement(Spacer, { height: 64 })),
            children)));
};

var v = "5.6.5";
var fr = 30;
var ip = 0;
var op = 60;
var w = 300;
var h = 300;
var nm = "izo-loading-spinner";
var ddd = 0;
var assets = [
	{
		id: "comp_0",
		layers: [
			{
				ddd: 0,
				ind: 1,
				ty: 4,
				nm: "logos Outlines",
				sr: 1,
				ks: {
					o: {
						a: 0,
						k: 100,
						ix: 11
					},
					r: {
						a: 1,
						k: [
							{
								i: {
									x: [
										0.239
									],
									y: [
										1
									]
								},
								o: {
									x: [
										0.809
									],
									y: [
										0
									]
								},
								t: 5,
								s: [
									0
								]
							},
							{
								t: 27.0000010997325,
								s: [
									-360
								]
							}
						],
						ix: 10
					},
					p: {
						a: 0,
						k: [
							500.01,
							500,
							0
						],
						ix: 2
					},
					a: {
						a: 0,
						k: [
							40.519,
							40.116,
							0
						],
						ix: 1
					},
					s: {
						a: 1,
						k: [
							{
								i: {
									x: [
										0.331,
										0.331,
										0.667
									],
									y: [
										1,
										1,
										1
									]
								},
								o: {
									x: [
										0.683,
										0.683,
										0.333
									],
									y: [
										0,
										0,
										0
									]
								},
								t: 0,
								s: [
									469.849,
									469.849,
									100
								]
							},
							{
								i: {
									x: [
										0.309,
										0.309,
										0.667
									],
									y: [
										1.675,
										1.675,
										1
									]
								},
								o: {
									x: [
										0.698,
										0.698,
										0.333
									],
									y: [
										0,
										0,
										0
									]
								},
								t: 15,
								s: [
									715.672,
									715.672,
									100
								]
							},
							{
								t: 30.0000012219251,
								s: [
									469.849,
									469.849,
									100
								]
							}
						],
						ix: 6
					}
				},
				ao: 0,
				shapes: [
					{
						ty: "gr",
						it: [
							{
								ind: 0,
								ty: "sh",
								ix: 1,
								ks: {
									a: 0,
									k: {
										i: [
											[
												1.408,
												-3.356
											],
											[
												7.788,
												0.384
											],
											[
												0.5,
												1.192
											],
											[
												2.781,
												5.359
											],
											[
												1.451,
												2.645
											],
											[
												0.902,
												1.524
											]
										],
										o: [
											[
												-1.09,
												2.595
											],
											[
												-0.463,
												-1.213
											],
											[
												-2.372,
												-5.588
											],
											[
												-1.393,
												-2.678
											],
											[
												-0.86,
												-1.547
											],
											[
												16.998,
												12.259
											]
										],
										v: [
											[
												34.366,
												-2.095
											],
											[
												18.997,
												0.765
											],
											[
												17.555,
												-2.844
											],
											[
												9.723,
												-19.209
											],
											[
												5.454,
												-27.191
											],
											[
												2.846,
												-31.814
											]
										],
										c: true
									},
									ix: 2
								},
								nm: "Path 1",
								mn: "ADBE Vector Shape - Group",
								hd: false
							},
							{
								ind: 1,
								ty: "sh",
								ix: 2,
								ks: {
									a: 0,
									k: {
										i: [
											[
												-0.196,
												-0.808
											],
											[
												-0.033,
												-1.343
											],
											[
												0.18,
												-0.384
											],
											[
												0.113,
												-0.074
											],
											[
												0.196,
												-0.02
											],
											[
												1.145,
												0.704
											],
											[
												1.103,
												0.938
											],
											[
												1.745,
												2.255
											],
											[
												0,
												0
											],
											[
												0,
												0
											],
											[
												0.054,
												0.049
											],
											[
												-0.005,
												0.008
											],
											[
												-4.554,
												-0.267
											]
										],
										o: [
											[
												0.339,
												1.417
											],
											[
												0.014,
												0.657
											],
											[
												-0.092,
												0.215
											],
											[
												-0.111,
												0.084
											],
											[
												-0.812,
												0.111
											],
											[
												-1.191,
												-0.718
											],
											[
												-2.195,
												-1.89
											],
											[
												0,
												0
											],
											[
												0,
												0
											],
											[
												-0.048,
												-0.059
											],
											[
												0.004,
												-0.01
											],
											[
												3.521,
												0.56
											],
											[
												0.237,
												0.805
											]
										],
										v: [
											[
												18.488,
												6.346
											],
											[
												19.131,
												10.551
											],
											[
												18.895,
												12.252
											],
											[
												18.631,
												12.601
											],
											[
												18.202,
												12.752
											],
											[
												14.857,
												11.559
											],
											[
												11.415,
												9.013
											],
											[
												5.398,
												2.728
											],
											[
												5.379,
												2.702
											],
											[
												5.352,
												2.67
											],
											[
												5.184,
												2.533
											],
											[
												5.199,
												2.506
											],
											[
												17.794,
												3.929
											]
										],
										c: true
									},
									ix: 2
								},
								nm: "Path 2",
								mn: "ADBE Vector Shape - Group",
								hd: false
							},
							{
								ind: 2,
								ty: "sh",
								ix: 3,
								ks: {
									a: 0,
									k: {
										i: [
											[
												0.196,
												0.809
											],
											[
												0.031,
												1.342
											],
											[
												-0.179,
												0.385
											],
											[
												-0.113,
												0.074
											],
											[
												-0.198,
												0.018
											],
											[
												-1.144,
												-0.703
											],
											[
												-1.104,
												-0.937
											],
											[
												-1.746,
												-2.255
											],
											[
												0,
												0
											],
											[
												-0.01,
												-0.011
											],
											[
												-0.052,
												-0.049
											],
											[
												0.005,
												-0.011
											],
											[
												4.551,
												0.267
											]
										],
										o: [
											[
												-0.339,
												-1.417
											],
											[
												-0.015,
												-0.657
											],
											[
												0.092,
												-0.214
											],
											[
												0.11,
												-0.085
											],
											[
												0.81,
												-0.113
											],
											[
												1.192,
												0.72
											],
											[
												2.194,
												1.891
											],
											[
												0,
												0
											],
											[
												0.009,
												0.011
											],
											[
												0.047,
												0.056
											],
											[
												-0.005,
												0.009
											],
											[
												-3.522,
												-0.561
											],
											[
												-0.237,
												-0.805
											]
										],
										v: [
											[
												-18.49,
												-6.347
											],
											[
												-19.132,
												-10.55
											],
											[
												-18.896,
												-12.252
											],
											[
												-18.632,
												-12.601
											],
											[
												-18.204,
												-12.751
											],
											[
												-14.859,
												-11.56
											],
											[
												-11.417,
												-9.014
											],
											[
												-5.4,
												-2.729
											],
											[
												-5.38,
												-2.702
											],
											[
												-5.354,
												-2.669
											],
											[
												-5.189,
												-2.535
											],
											[
												-5.204,
												-2.506
											],
											[
												-17.796,
												-3.931
											]
										],
										c: true
									},
									ix: 2
								},
								nm: "Path 3",
								mn: "ADBE Vector Shape - Group",
								hd: false
							},
							{
								ind: 3,
								ty: "sh",
								ix: 4,
								ks: {
									a: 0,
									k: {
										i: [
											[
												-0.501,
												-1.193
											],
											[
												-2.78,
												-5.36
											],
											[
												-1.451,
												-2.644
											],
											[
												-0.905,
												-1.528
											],
											[
												-1.152,
												2.748
											],
											[
												-7.789,
												-0.384
											]
										],
										o: [
											[
												2.372,
												5.588
											],
											[
												1.393,
												2.677
											],
											[
												0.861,
												1.551
											],
											[
												-20.397,
												-14.65
											],
											[
												1.089,
												-2.595
											],
											[
												0.463,
												1.214
											]
										],
										v: [
											[
												-17.557,
												2.844
											],
											[
												-9.725,
												19.209
											],
											[
												-5.456,
												27.191
											],
											[
												-2.84,
												31.829
											],
											[
												-34.368,
												2.094
											],
											[
												-18.999,
												-0.768
											]
										],
										c: true
									},
									ix: 2
								},
								nm: "Path 4",
								mn: "ADBE Vector Shape - Group",
								hd: false
							},
							{
								ind: 4,
								ty: "sh",
								ix: 5,
								ks: {
									a: 0,
									k: {
										i: [
											[
												11.796,
												8.425
											],
											[
												0,
												0.302
											],
											[
												2.218,
												0
											],
											[
												0,
												-2.22
											],
											[
												-2.22,
												0
											],
											[
												-0.593,
												0.346
											],
											[
												-0.91,
												-1.854
											],
											[
												-1.279,
												-2.723
											],
											[
												-2.111,
												-5.602
											],
											[
												-0.32,
												-0.922
											],
											[
												3.613,
												0.564
											],
											[
												2.976,
												0
											],
											[
												0.891,
												-0.557
											],
											[
												2.359,
												1.919
											],
											[
												1.413,
												0.799
											],
											[
												0.827,
												0.286
											],
											[
												1.129,
												-0.174
											],
											[
												0.316,
												-1.068
											],
											[
												-0.035,
												-0.841
											],
											[
												-0.418,
												-1.494
											],
											[
												-0.189,
												-0.581
											],
											[
												1.755,
												-4.181
											],
											[
												-11.795,
												-8.424
											],
											[
												0,
												-0.301
											],
											[
												-2.22,
												0
											],
											[
												0,
												2.219
											],
											[
												2.22,
												0
											],
											[
												0.594,
												-0.345
											],
											[
												0.91,
												1.855
											],
											[
												1.278,
												2.724
											],
											[
												2.112,
												5.602
											],
											[
												0.32,
												0.923
											],
											[
												-3.61,
												-0.563
											],
											[
												-2.981,
												0
											],
											[
												-0.892,
												0.562
											],
											[
												-2.358,
												-1.918
											],
											[
												-1.414,
												-0.799
											],
											[
												-0.827,
												-0.287
											],
											[
												-1.129,
												0.175
											],
											[
												-0.316,
												1.067
											],
											[
												0.036,
												0.84
											],
											[
												0.418,
												1.495
											],
											[
												0.187,
												0.579
											],
											[
												-0.74,
												0
											],
											[
												-1.587,
												3.785
											]
										],
										o: [
											[
												0.062,
												-0.282
											],
											[
												0,
												-2.22
											],
											[
												-2.22,
												0
											],
											[
												0,
												2.22
											],
											[
												0.736,
												0
											],
											[
												0.886,
												1.869
											],
											[
												1.339,
												2.697
											],
											[
												2.549,
												5.448
											],
											[
												0.341,
												0.915
											],
											[
												-3.619,
												-0.259
											],
											[
												-0.327,
												-2.888
											],
											[
												-1.128,
												0
											],
											[
												-1.974,
												-2.366
											],
											[
												-1.222,
												-0.975
											],
											[
												-0.709,
												-0.398
											],
											[
												-0.828,
												-0.269
											],
											[
												-1.109,
												0.094
											],
											[
												-0.376,
												1.025
											],
											[
												0.113,
												1.671
											],
											[
												0.169,
												0.591
											],
											[
												-7.936,
												-0.249
											],
											[
												-2.962,
												7.059
											],
											[
												-0.064,
												0.283
											],
											[
												0,
												2.219
											],
											[
												2.22,
												0
											],
											[
												0,
												-2.219
											],
											[
												-0.736,
												0
											],
											[
												-0.886,
												-1.869
											],
											[
												-1.34,
												-2.697
											],
											[
												-2.55,
												-5.449
											],
											[
												-0.341,
												-0.917
											],
											[
												3.616,
												0.259
											],
											[
												0.323,
												2.894
											],
											[
												1.132,
												0
											],
											[
												1.975,
												2.364
											],
											[
												1.223,
												0.975
											],
											[
												0.708,
												0.397
											],
											[
												0.828,
												0.269
											],
											[
												1.109,
												-0.094
											],
											[
												0.376,
												-1.026
											],
											[
												-0.113,
												-1.671
											],
											[
												-0.169,
												-0.59
											],
											[
												0.753,
												0.023
											],
											[
												7.062,
												0
											],
											[
												2.965,
												-7.058
											]
										],
										v: [
											[
												3.922,
												-34.972
											],
											[
												4.022,
												-35.847
											],
											[
												0.005,
												-39.866
											],
											[
												-4.016,
												-35.847
											],
											[
												0.005,
												-31.828
											],
											[
												2.014,
												-32.386
											],
											[
												4.74,
												-26.818
											],
											[
												8.671,
												-18.688
											],
											[
												15.777,
												-2.135
											],
											[
												16.749,
												0.626
											],
											[
												5.721,
												-0.647
											],
											[
												-0.005,
												-5.789
											],
											[
												-3.068,
												-4.896
											],
											[
												-9.555,
												-11.26
											],
											[
												-13.458,
												-13.964
											],
											[
												-15.736,
												-15.016
											],
											[
												-18.607,
												-15.368
											],
											[
												-21.277,
												-13.203
											],
											[
												-21.6,
												-10.453
											],
											[
												-20.699,
												-5.768
											],
											[
												-20.124,
												-4.034
											],
											[
												-37.307,
												0.861
											],
											[
												-3.923,
												34.971
											],
											[
												-4.025,
												35.847
											],
											[
												-0.005,
												39.866
											],
											[
												4.013,
												35.847
											],
											[
												-0.005,
												31.828
											],
											[
												-2.016,
												32.386
											],
											[
												-4.742,
												26.818
											],
											[
												-8.671,
												18.688
											],
											[
												-15.779,
												2.135
											],
											[
												-16.753,
												-0.628
											],
											[
												-5.734,
												0.644
											],
											[
												-0.005,
												5.797
											],
											[
												3.066,
												4.898
											],
											[
												9.553,
												11.26
											],
											[
												13.458,
												13.964
											],
											[
												15.734,
												15.016
											],
											[
												18.605,
												15.368
											],
											[
												21.275,
												13.204
											],
											[
												21.598,
												10.454
											],
											[
												20.699,
												5.767
											],
											[
												20.124,
												4.035
											],
											[
												22.368,
												4.075
											],
											[
												37.303,
												-0.861
											]
										],
										c: true
									},
									ix: 2
								},
								nm: "Path 5",
								mn: "ADBE Vector Shape - Group",
								hd: false
							},
							{
								ty: "mm",
								mm: 1,
								nm: "Merge Paths 1",
								mn: "ADBE Vector Filter - Merge",
								hd: false
							},
							{
								ty: "fl",
								c: {
									a: 0,
									k: [
										1,
										1,
										1,
										1
									],
									ix: 4
								},
								o: {
									a: 0,
									k: 100,
									ix: 5
								},
								r: 1,
								bm: 0,
								nm: "Fill 1",
								mn: "ADBE Vector Graphic - Fill",
								hd: false
							},
							{
								ty: "tr",
								p: {
									a: 0,
									k: [
										40.518,
										40.116
									],
									ix: 2
								},
								a: {
									a: 0,
									k: [
										0,
										0
									],
									ix: 1
								},
								s: {
									a: 0,
									k: [
										100,
										100
									],
									ix: 3
								},
								r: {
									a: 0,
									k: 0,
									ix: 6
								},
								o: {
									a: 1,
									k: [
										{
											i: {
												x: [
													0.833
												],
												y: [
													0.833
												]
											},
											o: {
												x: [
													0.167
												],
												y: [
													0.167
												]
											},
											t: 8,
											s: [
												99
											]
										},
										{
											i: {
												x: [
													0.833
												],
												y: [
													0.833
												]
											},
											o: {
												x: [
													0.167
												],
												y: [
													0.167
												]
											},
											t: 16,
											s: [
												11
											]
										},
										{
											t: 25.0000010182709,
											s: [
												100
											]
										}
									],
									ix: 7
								},
								sk: {
									a: 1,
									k: [
										{
											i: {
												x: [
													0.833
												],
												y: [
													0.833
												]
											},
											o: {
												x: [
													0.167
												],
												y: [
													0.167
												]
											},
											t: 13,
											s: [
												0
											]
										},
										{
											i: {
												x: [
													0.833
												],
												y: [
													0.833
												]
											},
											o: {
												x: [
													0.167
												],
												y: [
													0.167
												]
											},
											t: 16,
											s: [
												32
											]
										},
										{
											t: 19.0000007738859,
											s: [
												0
											]
										}
									],
									ix: 4
								},
								sa: {
									a: 0,
									k: 0,
									ix: 5
								},
								nm: "Transform"
							}
						],
						nm: "Group 1",
						np: 7,
						cix: 2,
						bm: 0,
						ix: 1,
						mn: "ADBE Vector Group",
						hd: false
					}
				],
				ip: 0,
				op: 300.00001221925,
				st: 0,
				bm: 0
			}
		]
	}
];
var layers = [
	{
		ddd: 0,
		ind: 1,
		ty: 0,
		nm: "logos Outlines Comp 1",
		refId: "comp_0",
		sr: 1,
		ks: {
			o: {
				a: 0,
				k: 100,
				ix: 11
			},
			r: {
				a: 0,
				k: 0,
				ix: 10
			},
			p: {
				a: 0,
				k: [
					150,
					150,
					0
				],
				ix: 2
			},
			a: {
				a: 0,
				k: [
					500,
					500,
					0
				],
				ix: 1
			},
			s: {
				a: 0,
				k: [
					30,
					30,
					100
				],
				ix: 6
			}
		},
		ao: 0,
		w: 1000,
		h: 1000,
		ip: 0,
		op: 300.00001221925,
		st: 0,
		bm: 0
	}
];
var markers = [
];
var loadingAnimation = {
	v: v,
	fr: fr,
	ip: ip,
	op: op,
	w: w,
	h: h,
	nm: nm,
	ddd: ddd,
	assets: assets,
	layers: layers,
	markers: markers
};

const Loading = () => {
    return (React__default.createElement(FlexContainer, { id: 'Loading', primaryAlign: 'center' },
        React__default.createElement(Lottie, { loop: true, animationData: loadingAnimation, play: true })));
};

export { Button, Crash, DefaultRight, EmailOptIn, ExternalLink, FlexContainer, Footer, Header, Image, Input, LOCAL_STORAGE_KEY, Loading, Login, Modal, Spacer, Text };
